import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { auth, db, storage } from '../firebase';
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  deleteDoc,
  increment,
  limit,
  startAfter,
  getDocs,
  setDoc,
  where,
  Timestamp
} from 'firebase/firestore';
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytesResumable
} from 'firebase/storage';

// ===============================================
// Helpers & Handlers
// ===============================================
function getLocalContacts() {
  try { return JSON.parse(localStorage.getItem('nexora_local_contacts') || '{}'); } catch (e) { return {}; }
}
function saveLocalContact(phoneKey, name) {
  try {
    const contacts = getLocalContacts();
    contacts[phoneKey] = name;
    localStorage.setItem('nexora_local_contacts', JSON.stringify(contacts));
  } catch (e) {}
}
function getCachedData(key) {
  try { return JSON.parse(localStorage.getItem(key)) || []; } catch(e) { return []; }
}

function getBlockStatusCache(myPhone, targetPhone) {
  try {
    const cache = JSON.parse(localStorage.getItem(`nexora_block_cache_${myPhone}`) || '{}');
    return cache[targetPhone] || false;
  } catch (e) {
    return false;
  }
}

function saveBlockStatusCache(myPhone, targetPhone, status) {
  try {
    const cache = JSON.parse(localStorage.getItem(`nexora_block_cache_${myPhone}`) || '{}');
    cache[targetPhone] = status;
    localStorage.setItem(`nexora_block_cache_${myPhone}`, JSON.stringify(cache));
  } catch (e) {}
}

function getChatCache(roomId) {
  try {
    if (!roomId) return { messages: [], unreadIds: [], unreadCount: 0, savedAt: 0 };
    const raw = localStorage.getItem(`nexora_p2p_msgs_${roomId}`);
    if (!raw) return { messages: [], unreadIds: [], unreadCount: 0, savedAt: 0 };

    const parsed = JSON.parse(raw);

    if (Array.isArray(parsed)) {
      return { messages: parsed, unreadIds: [], unreadCount: 0, savedAt: 0 };
    }

    return {
      messages: Array.isArray(parsed.messages) ? parsed.messages : [],
      unreadIds: Array.isArray(parsed.unreadIds) ? parsed.unreadIds : [],
      unreadCount: Number(parsed.unreadCount) || 0,
      savedAt: Number(parsed.savedAt) || 0
    };
  } catch (e) {
    return { messages: [], unreadIds: [], unreadCount: 0, savedAt: 0 };
  }
}

function saveChatCache(roomId, messages, unreadIds = [], unreadCount = unreadIds.length) {
  try {
    if (!roomId || !Array.isArray(messages)) return;

    const cachedMessages = messages.length > 100 ? messages.slice(-100) : messages;

    localStorage.setItem(
      `nexora_p2p_msgs_${roomId}`,
      JSON.stringify({
        messages: cachedMessages,
        unreadIds: Array.isArray(unreadIds) ? unreadIds.slice(-50) : [],
        unreadCount: Number(unreadCount) || 0,
        savedAt: Date.now()
      })
    );
  } catch (e) {
    console.warn('Chat cache save skipped:', e);
  }
}

const IMGBB_API_KEY = "af3aa5305ed243a9489b195b87a2afe5"; 

const uploadToImgBB = async (file) => {
  const formData = new FormData();
  formData.append('image', file);
  try {
    const res = await fetch(`https://api.imgbb.com/1/upload?key=${IMGBB_API_KEY}`, { method: 'POST', body: formData });
    const data = await res.json();
    return data.data?.url || null;
  } catch (e) { return null; }
};

const WEBRTC_SIGNAL_COLLECTION = 'webrtc_signaling';
const WEBRTC_PENDING_QUEUE_KEY = 'nexora_webrtc_pending_queue';
const WEBRTC_CHUNK_SIZE = 16 * 1024;
const WEBRTC_ICE_SERVERS = [
  {
    urls: [
      'stun:stun.l.google.com:19302',
      'stun:stun1.l.google.com:19302',
      'stun:stun2.l.google.com:19302',
      'stun:stun3.l.google.com:19302',
      'stun:stun4.l.google.com:19302'
    ]
  },
  {
    urls: 'turn:nexora-turn.example.com:3478',
    username: 'nexora-demo',
    credential: 'nexora-demo-password'
  }
];

const createRtcId = (prefix = 'rtc') => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const safeParse = (value, fallback = []) => {
  try {
    const parsed = JSON.parse(value);
    return parsed ?? fallback;
  } catch (e) {
    return fallback;
  }
};

const getPendingWebrtcQueue = (roomId) => {
  if (!roomId) return [];
  try {
    const raw = localStorage.getItem(`${WEBRTC_PENDING_QUEUE_KEY}_${roomId}`);
    const parsed = safeParse(raw, []);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const savePendingWebrtcQueue = (roomId, queue) => {
  if (!roomId) return;
  try {
    localStorage.setItem(`${WEBRTC_PENDING_QUEUE_KEY}_${roomId}`, JSON.stringify(queue));
  } catch (e) {
    console.warn('WebRTC queue save failed:', e);
  }
};

const queuePendingWebrtcMessage = (roomId, message) => {
  if (!roomId || !message) return;
  const queue = getPendingWebrtcQueue(roomId);
  queue.push({ ...message, queuedAt: Date.now(), attempts: 0, status: 'pending' });
  savePendingWebrtcQueue(roomId, queue);
};

const removeQueuedWebrtcMessage = (roomId, messageId) => {
  const queue = getPendingWebrtcQueue(roomId).filter(item => item.id !== messageId);
  savePendingWebrtcQueue(roomId, queue);
};

const sha256 = async (payload) => {
  try {
    const text = typeof payload === 'string' ? payload : JSON.stringify(payload);
    const data = new TextEncoder().encode(text);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
  } catch (e) {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
};

const readFileAsArrayBuffer = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error('File read failed'));
  reader.readAsArrayBuffer(file);
});

const arrayBufferToBase64 = (buffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
};

const base64ToUint8Array = (value) => {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const createChunkedTransfer = async (payload, kind, metadata = {}) => {
  const bytes = payload instanceof Blob ? await payload.arrayBuffer() : payload;
  const source = new Uint8Array(bytes);
  const totalChunks = Math.ceil(source.length / WEBRTC_CHUNK_SIZE);
  const transferId = createRtcId('transfer');
  const fileHash = await sha256(Array.from(source));

  return {
    transferId,
    kind,
    totalChunks,
    chunkSize: WEBRTC_CHUNK_SIZE,
    fileHash,
    createdAt: Date.now(),
    metadata,
    chunks: Array.from({ length: totalChunks }, (_, index) => {
      const start = index * WEBRTC_CHUNK_SIZE;
      const end = Math.min(start + WEBRTC_CHUNK_SIZE, source.length);
      return {
        index,
        transferId,
        totalChunks,
        chunk: arrayBufferToBase64(source.slice(start, end).buffer),
        fileHash,
        kind,
        metadata
      };
    })
  };
};

const buildWebrtcConfig = () => ({
  iceServers: WEBRTC_ICE_SERVERS,
  iceCandidatePoolSize: 0
});

const isWebrtcAvailable = () => typeof window !== 'undefined' && !!(window.RTCPeerConnection || window.webkitRTCPeerConnection);

const createP2PMessage = (type, payload) => ({
  kind: 'nexora_webrtc_message',
  version: 1,
  type,
  payload,
  id: createRtcId('msg'),
  ts: Date.now()
});

const normalizeIncomingMessage = (value) => {
  try {
    if (typeof value === 'string') return JSON.parse(value);
    if (value instanceof Blob) return JSON.parse(value ? value.text ? value.text() : '' : '{}');
    return value;
  } catch (e) {
    return null;
  }
};

const ensureRtcPulse = async (pc) => {
  if (!pc || pc.connectionState === 'connected') return true;
  try {
    await pc.getStats();
    return true;
  } catch (e) {
    return false;
  }
};

const compressImage = (file, isSticker = false) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width; let height = img.height; 
        const maxWidth = isSticker ? 512 : 1200; 
        if (width > maxWidth) { height = Math.round((height * maxWidth) / width); width = maxWidth; }
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL(isSticker ? 'image/png' : 'image/jpeg', 0.85));
      };
    };
  });
};

const buildRealWaveform = async (audioBlob) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const arrayBuffer = await audioBlob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    const rawData = audioBuffer.getChannelData(0);
    const samples = 25;
    const blockSize = Math.floor(rawData.length / samples);
    const filteredData = [];
    for (let i = 0; i < samples; i++) {
      let blockStart = blockSize * i;
      let sum = 0;
      for (let j = 0; j < blockSize; j++) { sum = sum + Math.abs(rawData[blockStart + j]); }
      filteredData.push(Math.max(6, Math.min(36, (sum / blockSize) * 200)));
    }
    return filteredData;
  } catch (e) {
    return Array.from({ length: 25 }, () => Math.floor(Math.random() * 15) + 10);
  }
};

const makeFallbackWaveform = (id) => {
  const seed = (id || "default").split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return Array.from({ length: 25 }, (_, i) => Math.floor(Math.abs(Math.sin(seed + i) * 20) + 10));
};

 const normalizePhone = (p) => {
    if (!p) return '';
    let d = p.replace(/\D/g, '');
    if (d.startsWith('880')) d = d.substring(3);
    if (d.startsWith('0')) d = d.substring(1);
    return `+880${d}`;
  };

const ChatScreen = () => {

  const navigate = useNavigate();
  const location = useLocation();
  const { chatId: targetUid } = useParams();
  
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const rtcPeerRef = useRef(null);
  const rtcDataChannelRef = useRef(null);
  const rtcSignalUnsubscribeRef = useRef(null);
  const rtcLocalAudioStreamRef = useRef(null);
  const rtcRemoteAudioStreamRef = useRef(null);
  const rtcTransferStateRef = useRef({});
  const rtcQueueProcessingRef = useRef(false);
  const rtcCallStateRef = useRef({ status: 'idle', type: 'audio' });

  const getRoomId = (uid1, uid2) => [uid1, uid2].sort().join('_');

  const getRtcSignalDoc = (roomId) => (roomId ? doc(db, WEBRTC_SIGNAL_COLLECTION, roomId) : null);

  const appendLocalP2PMessage = (message) => {
    if (!message || !message.id || !message.type) return;
    setMessages(prev => {
      const exists = prev.some(item => item.id === message.id);
      if (exists) return prev;
      const next = [...prev, message];
      saveChatCache(roomIdRef.current || 'temp', next);
      return next;
    });
  };

  const queueP2PTransfer = (kind, payload, metadata = {}) => {
    const roomKey = roomIdRef.current;
    if (!roomKey) return null;
    const message = {
      id: createRtcId('queue'),
      roomId: roomKey,
      kind,
      payload,
      metadata,
      queuedAt: Date.now(),
      status: 'pending'
    };
    queuePendingWebrtcMessage(roomKey, message);
    return message;
  };

  const sendWebrtcMessage = (message) => {
    const dc = rtcDataChannelRef.current;
    if (!message || !dc || dc.readyState !== 'open') {
      const roomKey = roomIdRef.current;
      if (roomKey) queuePendingWebrtcMessage(roomKey, message);
      return false;
    }

    try {
      dc.send(JSON.stringify(message));
      return true;
    } catch (e) {
      console.warn('WebRTC send failed:', e);
      const roomKey = roomIdRef.current;
      if (roomKey) queuePendingWebrtcMessage(roomKey, message);
      return false;
    }
  };

  const flushPendingWebrtcQueue = async () => {
    if (!currentUid || !targetUid || rtcQueueProcessingRef.current || !rtcDataChannelRef.current) return;
    const roomKey = getRoomId(currentUid, targetUid);
    const queue = getPendingWebrtcQueue(roomKey);
    if (!queue.length) return;

    rtcQueueProcessingRef.current = true;
    try {
      for (const item of queue) {
        if (!item || !item.kind) continue;
        const delivered = sendWebrtcMessage(item);
        if (delivered) removeQueuedWebrtcMessage(roomKey, item.id);
      }
    } finally {
      rtcQueueProcessingRef.current = false;
    }
  };

  const handleIncomingP2PMessage = async (event) => {
    try {
      const raw = typeof event === 'string' ? event : event?.data;
      if (!raw) return;
      const message = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!message || message.kind !== 'nexora_webrtc_message') return;

      if (message.type === 'text') {
        const textPayload = message.payload || {};
        appendLocalP2PMessage({
          id: textPayload.id || message.id,
          senderId: targetUid,
          senderName: targetUserData?.fullName || 'User',
          senderPhoto: targetUserData?.photoURL || '',
          type: 'text',
          text: textPayload.text || '',
          status: 'delivered',
          createdAt: { seconds: Math.floor(Date.now() / 1000) },
          timeStr: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })
        });
        sendWebrtcMessage(createP2PMessage('ack', { id: message.id }));
      }

      if (message.type === 'file' || message.type === 'image' || message.type === 'video' || message.type === 'voice') {
        const transfer = message.payload || {};
        const mediaPayload = transfer.media || {};
        appendLocalP2PMessage({
          id: transfer.id || message.id,
          senderId: targetUid,
          senderName: targetUserData?.fullName || 'User',
          senderPhoto: targetUserData?.photoURL || '',
          type: message.type,
          text: mediaPayload.fileName || mediaPayload.title || 'Media',
          mediaData: mediaPayload.dataUrl || mediaPayload.url || '',
          fileName: mediaPayload.fileName || 'file',
          fileSize: mediaPayload.fileSize || 0,
          mimeType: mediaPayload.mimeType || 'application/octet-stream',
          audioData: mediaPayload.dataUrl || mediaPayload.url || '',
          waveform: mediaPayload.waveform || makeFallbackWaveform(transfer.id || message.id),
          duration: mediaPayload.duration || '0s',
          status: 'delivered',
          createdAt: { seconds: Math.floor(Date.now() / 1000) },
          timeStr: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })
        });
        sendWebrtcMessage(createP2PMessage('ack', { id: message.id }));
      }

      if (message.type === 'ack') {
        const queueKey = roomIdRef.current;
        if (queueKey) removeQueuedWebrtcMessage(queueKey, message.payload?.id);
      }
    } catch (e) {
      console.warn('Incoming WebRTC message parse failed:', e);
    }
  };

  const bindRtcDataChannel = (channel) => {
    if (!channel) return;
    rtcDataChannelRef.current = channel;
    channel.binaryType = 'arraybuffer';
    channel.onopen = () => {
      flushPendingWebrtcQueue();
    };
    channel.onmessage = (event) => handleIncomingP2PMessage(event);
    channel.onclose = () => {
      rtcDataChannelRef.current = null;
    };
    channel.onerror = (error) => {
      console.warn('RTC DataChannel error:', error);
    };
  };

  const createRtcPeerConnection = async () => {
    if (!isWebrtcAvailable() || !currentUid || !targetUid) return null;
    if (rtcPeerRef.current) {
      return rtcPeerRef.current;
    }

    const pc = new (window.RTCPeerConnection || window.webkitRTCPeerConnection)(buildWebrtcConfig());
    rtcPeerRef.current = pc;

    pc.ondatachannel = (event) => bindRtcDataChannel(event.channel);
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') {
        flushPendingWebrtcQueue();
      }
    };
    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
        try { pc.restartIce?.(); } catch (e) {}
      }
    };
    pc.onicecandidate = async (event) => {
      if (!event.candidate) return;
      const signalRef = getRtcSignalDoc(getRoomId(currentUid, targetUid));
      if (!signalRef) return;
      try {
        await updateDoc(signalRef, {
          candidates: arrayUnion({
            from: currentUid,
            to: targetUid,
            candidate: event.candidate.toJSON(),
            createdAt: Date.now()
          })
        });
      } catch (e) {
        console.warn('WebRTC candidate update failed:', e);
      }
    };

    pc.ontrack = (event) => {
      const stream = event.streams?.[0];
      if (stream) {
        rtcRemoteAudioStreamRef.current = stream;
        const audio = new Audio();
        audio.srcObject = stream;
        audio.autoplay = true;
        audio.muted = false;
        audio.play().catch(() => {});
      }
    };

    const dc = pc.createDataChannel('nexora-p2p', { ordered: true, maxRetransmits: 3 });
    bindRtcDataChannel(dc);

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    const signalRef = getRtcSignalDoc(getRoomId(currentUid, targetUid));
    if (signalRef) {
      await setDoc(signalRef, {
        offer: { from: currentUid, to: targetUid, sdp: offer.sdp, type: offer.type, createdAt: Date.now() },
        updatedAt: Date.now()
      }, { merge: true });
    }

    return pc;
  };

  const attachRtcSignalListener = () => {
    if (!currentUid || !targetUid) return;
    const roomKey = getRoomId(currentUid, targetUid);
    const signalRef = getRtcSignalDoc(roomKey);
    if (!signalRef || rtcSignalUnsubscribeRef.current) return;

    rtcSignalUnsubscribeRef.current = onSnapshot(signalRef, async (snapshot) => {
      if (!snapshot.exists()) return;
      const data = snapshot.data();
      const peerPc = rtcPeerRef.current || await createRtcPeerConnection();
      if (!peerPc) return;

      if (data?.offer && data.offer.from !== currentUid && !peerPc.remoteDescription) {
        try {
          await peerPc.setRemoteDescription(new RTCSessionDescription({ type: data.offer.type, sdp: data.offer.sdp }));
          const answer = await peerPc.createAnswer();
          await peerPc.setLocalDescription(answer);
          await updateDoc(signalRef, {
            answer: { from: currentUid, to: targetUid, sdp: answer.sdp, type: answer.type, createdAt: Date.now() },
            updatedAt: Date.now()
          });
        } catch (e) {
          console.warn('WebRTC offer handling failed:', e);
        }
      }

      if (data?.answer && data.answer.from !== currentUid && peerPc.remoteDescription && !peerPc.remoteDescription?.sdp?.includes('a=setup:actpass')) {
        try {
          await peerPc.setRemoteDescription(new RTCSessionDescription({ type: data.answer.type, sdp: data.answer.sdp }));
        } catch (e) {
          console.warn('WebRTC answer handling failed:', e);
        }
      }

      if (Array.isArray(data?.candidates)) {
        for (const item of data.candidates) {
          if (!item || item.from === currentUid || !item.candidate) continue;
          try {
            await peerPc.addIceCandidate(new RTCIceCandidate(item.candidate));
          } catch (e) {
            console.warn('WebRTC candidate add failed:', e);
          }
        }
      }
    });
  };

  const startP2PAudioCall = async (type = 'audio') => {
    if (!isWebrtcAvailable() || !currentUid || !targetUid) return null;
    rtcCallStateRef.current = { status: 'connecting', type };

    try {
      const peerPc = await createRtcPeerConnection();
      if (!peerPc) return null;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: type === 'video' });
      rtcLocalAudioStreamRef.current = stream;
      stream.getTracks().forEach(track => peerPc.addTrack(track, stream));

      await flushPendingWebrtcQueue();
      return peerPc;
    } catch (e) {
      console.warn('RTC call start failed:', e);
      rtcCallStateRef.current = { status: 'failed', type };
      return null;
    }
  };

  const sendP2PText = (textValue) => {
    if (!textValue || !currentUid || !targetUid) return false;
    const payload = createP2PMessage('text', { id: createRtcId('text'), text: textValue });
    return sendWebrtcMessage(payload);
  };

  const sendP2PMedia = async (mediaType, mediaInfo) => {
    const roomKey = getRoomId(currentUid, targetUid);
    if (!roomKey) return false;
    const payload = createP2PMessage(mediaType, { id: createRtcId(mediaType), media: mediaInfo });
    if (rtcDataChannelRef.current?.readyState !== 'open') {
      queuePendingWebrtcMessage(roomKey, payload);
      return false;
    }
    return sendWebrtcMessage(payload);
  };
  const [currentUid, setCurrentUid] = useState(null);
  const roomIdRef = useRef('');

  // Block States
  // Block States - ক্যাশ থেকে ইনস্ট্যান্ট লোড হবে
const [hasBlockedTarget, setHasBlockedTarget] = useState(() => {
  const myPhone = normalizePhone(localStorage.getItem('nexora_user_phone') || '');
  const targetPhone = normalizePhone(targetUid); // targetUid useParams থেকে আসে
  return getBlockStatusCache(myPhone, targetPhone);
});

const [isBlockedByTarget, setIsBlockedByTarget] = useState(() => {
  const myPhone = normalizePhone(localStorage.getItem('nexora_user_phone') || '');
  const targetPhone = normalizePhone(targetUid);
  // আপনি চাইলে target_blocked_cache নামে আলাদা কি রাখতে পারেন
  return getBlockStatusCache(targetPhone, myPhone); 
});

  // Custom Toast State
  const [toastMsg, setToastMsg] = useState(null);
  const toastTimerRef = useRef(null);

  const showToast = (msg) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToastMsg(msg);
    toastTimerRef.current = setTimeout(() => setToastMsg(null), 2000);
  };

  const [text, setText] = useState('');

  // Hydrate messages instantly from Dashboard-preloaded cache or local cache.
  // The full message objects already contain senderName/senderPhoto, so the
  // first ChatScreen render does not need to resolve message profiles.
  const preloadedChatCacheRef = useRef(null);

  const [messages, setMessages] = useState(() => {
    try {
      if (location.state?.preloadedChatCache) {
        preloadedChatCacheRef.current = location.state.preloadedChatCache;
        if (Array.isArray(location.state.preloadedChatCache.messages)) {
          return location.state.preloadedChatCache.messages;
        }
      }

      if (location.state?.preloadedMessages && location.state.preloadedMessages.length > 0) {
        return location.state.preloadedMessages;
      }

      const uid = localStorage.getItem('nexora_user_uid') || auth.currentUser?.uid;
      if (!uid || !targetUid) return [];

      const rId = [uid, targetUid].sort().join('_');
      const cached = getChatCache(rId);
      preloadedChatCacheRef.current = cached;
      return cached.messages;
    } catch (e) {
      return [];
    }
  });

  // Pre-hydrate target user data directly to avoid header multi-step flicker
  const [targetUserData, setTargetUserData] = useState(() => {
    if (location.state?.targetUserData) {
      const t = location.state.targetUserData;
      return {
        fullName: t.fullName || t.title || t.name || t.phoneNumber,
        photoURL: t.photoURL || t.photo || t.avatar || t.iconURL,
        phoneNumber: t.phoneNumber || t.phone,
        isOnline: t.isOnline || false,
        role: t.role || 'user'
      };
    }
    const savedChats = getCachedData('nexora_dash_chats');
    const found = savedChats.find(c => c.id === targetUid || c.uid === targetUid || c.otherUid === targetUid);
    if (found) {
      return {
        fullName: found.title || found.name || found.fullName,
        photoURL: found.photoURL || found.photo || found.avatar,
        phoneNumber: found.phoneNumber || found.phone,
        isOnline: found.isOnline || false
      };
    }
    return null;
  });

  const [currentUserData, setCurrentUserData] = useState(null);
  const [isTargetTyping, setIsTargetTyping] = useState(false);
  const [userProfilesMap, setUserProfilesMap] = useState({});
  
  const [highlightedMsgId, setHighlightedMsgId] = useState(null); 
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [lastVisibleMsg, setLastVisibleMsg] = useState(null);
  const [hasMoreMsgs, setHasMoreMsgs] = useState(true);
  const [isFetchingOld, setIsFetchingOld] = useState(false);

  // Unread & Auto-scroll State
  const firstUnreadIdRef = useRef(null);
  const totalUnreadCountRef = useRef(0);
  const unreadAnchorInitializedRef = useRef(false);

  // Keep the unread boundary stable even when Firestore changes message.status
  // from 'unread' to 'seen' immediately after the chat opens.
  const getMessageMillis = (msg) => {
    if (!msg?.createdAt) return 0;
    if (typeof msg.createdAt === 'number') return msg.createdAt;
    if (typeof msg.createdAt?.toMillis === 'function') return msg.createdAt.toMillis();
    if (typeof msg.createdAt?.seconds === 'number') {
      return msg.createdAt.seconds * 1000 + Math.floor((msg.createdAt.nanoseconds || 0) / 1000000);
    }
    const parsed = new Date(msg.createdAt).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  };

  // Calculate the FIRST unread message from chronological message order.
  // This runs during render so the unread anchor exists before the first paint.
  try {
    const cachedUid = localStorage.getItem('nexora_user_uid') || auth.currentUser?.uid;
    if (cachedUid && targetUid && !unreadAnchorInitializedRef.current && messages.length > 0) {
      const preloadedCache = preloadedChatCacheRef.current;

      // Prefer Dashboard's unreadIds/count because they were captured before
      // ChatScreen opened and before Firestore status can be changed to seen.
      const cachedUnreadIds = Array.isArray(preloadedCache?.unreadIds)
        ? preloadedCache.unreadIds
        : [];

      const cachedUnreadMessages = cachedUnreadIds
        .map(id => messages.find(m => m.id === id))
        .filter(Boolean)
        .sort((a, b) => {
          const at = getMessageMillis(a);
          const bt = getMessageMillis(b);
          if (at && bt && at !== bt) return at - bt;
          return 0;
        });

      if (cachedUnreadMessages.length > 0) {
        firstUnreadIdRef.current = cachedUnreadMessages[0].id;
        totalUnreadCountRef.current =
          Number(preloadedCache?.unreadCount) || cachedUnreadMessages.length;
        unreadAnchorInitializedRef.current = true;
      } else {
        const unreadList = messages
          .filter(m => m.senderId !== cachedUid && m.status !== 'seen')
          .sort((a, b) => {
            const at = getMessageMillis(a);
            const bt = getMessageMillis(b);
            if (at && bt && at !== bt) return at - bt;
            return 0;
          });

        if (unreadList.length > 0) {
          firstUnreadIdRef.current = unreadList[0].id;
          totalUnreadCountRef.current = unreadList.length;
          unreadAnchorInitializedRef.current = true;
        }
      }
    }
  } catch (e) {}

  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const autoScrollLockedRef = useRef(false);

  // Media & Upload Progress
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Contacts
  const [isSavedContact, setIsSavedContact] = useState(() => {
    try {
      const localContacts = getLocalContacts();
      const phone = location.state?.targetUserData?.phoneNumber || targetUserData?.phoneNumber;
      if (phone) {
        let d = phone.replace(/\D/g, '');
        if (d.startsWith('880')) d = d.substring(3);
        if (d.startsWith('0')) d = d.substring(1);
        return !!localContacts[`+880${d}`];
      }
    } catch (e) {}
    return true; // Default true to prevent banner flicker
  });

  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [newContactName, setNewContactName] = useState('');
  const [selectedPhoneInfo, setSelectedPhoneInfo] = useState(null);

  // Pinned Messages (Max 3)
  const [pinnedMsgIds, setPinnedMsgIds] = useState([]);
  const [activePinIndex, setActivePinIndex] = useState(0);

  // Voice Recorder
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [waveBars, setWaveBars] = useState([12, 22, 35, 18, 30, 10, 28, 40, 15, 18]);
  const [playingVoiceId, setPlayingVoiceId] = useState(null);
  const [voicePlayback, setVoicePlayback] = useState({});

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animFrameRef = useRef(null);

  // Modals, Actions & Multi-Select
  const [fullScreenMedia, setFullScreenMedia] = useState(null);
  const [replyingToMsg, setReplyingToMsg] = useState(null);
  const [editingMsg, setEditingMsg] = useState(null);
  const [editInputText, setEditInputText] = useState('');
  const [contextMsg, setContextMsg] = useState(null);
  const [messageActionModal, setMessageActionModal] = useState(null); 
  const [msgDetails, setMsgDetails] = useState(null); 
  const [reactionPopup, setReactionPopup] = useState(null);
  const [customEmojiMsgId, setCustomEmojiMsgId] = useState(null);
  const [swipeX, setSwipeX] = useState({});
  const [selectedMessageIds, setSelectedMessageIds] = useState([]); 

  // Forward Modal State
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [forwardTargets, setForwardTargets] = useState([]);
  const [selectedForwardIds, setSelectedForwardIds] = useState([]);
  const [msgToForward, setMsgToForward] = useState(null); 

  // Stickers State
  const defaultStickers = [
    "https://cdn-icons-png.flaticon.com/512/4860/4860953.png",
    "https://cdn-icons-png.flaticon.com/512/4860/4860927.png",
    "https://cdn-icons-png.flaticon.com/512/10543/10543085.png"
  ];
  const [savedStickers, setSavedStickers] = useState(getCachedData('nexora_saved_stickers').length ? getCachedData('nexora_saved_stickers') : defaultStickers);
  const [showStickerModal, setShowStickerModal] = useState(false);
  const stickerImportRef = useRef(null);
  const stickerLongPressTimerRef = useRef(null);

  const [showMeetForm, setShowMeetForm] = useState(false);
  const [meetCompanyName, setMeetCompanyName] = useState('Selfgars');
  const [meetTime, setMeetTime] = useState('২:৩০');
  const [meetLinkInput, setMeetLinkInput] = useState('');

  const isInitialLoadRef = useRef(true);
  const justMounted = useRef(true); 
  
  const clickTimers = useRef({});
  const clickCounts = useRef({});
  const msgLongPressTimerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);

  const mediaInputRef = useRef(null); 
  const fileInputRef = useRef(null);  
  const localContacts = getLocalContacts();

  useEffect(() => {
    const timer = setTimeout(() => { justMounted.current = false; }, 800);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
  if (!currentUserData?.phoneNumber || !targetUserData?.phoneNumber) return;

  const myPhone = normalizePhone(currentUserData.phoneNumber);
  const targetPhone = normalizePhone(targetUserData.phoneNumber);

  // আমি তাকে ব্লক করেছি কিনা
  const iBlockedRef = doc(db, 'block_list', myPhone, 'blocked_users', targetPhone);
  const unsubIBlocked = onSnapshot(iBlockedRef, (snap) => {
    const status = snap.exists();
    setHasBlockedTarget(status);
    saveBlockStatusCache(myPhone, targetPhone, status); // ক্যাশে সেভ হলো
  });

  // সে আমাকে ব্লক করেছে কিনা
  const targetBlockedRef = doc(db, 'block_list', targetPhone, 'blocked_users', myPhone);
  const unsubTargetBlocked = onSnapshot(targetBlockedRef, (snap) => {
    const status = snap.exists();
    setIsBlockedByTarget(status);
    saveBlockStatusCache(targetPhone, myPhone, status); // ক্যাশে সেভ হলো
  });

  return () => {
    unsubIBlocked();
    unsubTargetBlocked();
  };
}, [currentUserData?.phoneNumber, targetUserData?.phoneNumber]);

  useEffect(() => {
    isInitialLoadRef.current = true;
    setHasMoreMsgs(true);
    setLastVisibleMsg(null);
    firstUnreadIdRef.current = null;
    totalUnreadCountRef.current = 0;
    unreadAnchorInitializedRef.current = false;

    const userUid = localStorage.getItem('nexora_user_uid') || auth.currentUser?.uid;
    if (userUid && targetUid) {
      const rId = [userUid, targetUid].sort().join('_');
      const cached = getChatCache(rId);

      if (
        cached.messages.length > 0 &&
        (!location.state?.preloadedMessages || location.state.preloadedMessages.length === 0) &&
        !location.state?.preloadedChatCache
      ) {
        preloadedChatCacheRef.current = cached;
        setMessages(cached.messages);
      } else if (location.state?.preloadedChatCache) {
        preloadedChatCacheRef.current = location.state.preloadedChatCache;
      }
    }
  }, [targetUid]);

 

  const handleNavigate = (path, state = {}) => {
    navigate(path, state);
  };

  const joinCommunityInvite = async (communityId) => {
    if (!communityId || !currentUid) return;
    try {
      const commRef = doc(db, 'communities', communityId);
      const commSnap = await getDoc(commRef);
      if (!commSnap.exists()) {
        showToast('এই কমিউনিটিটি আর নেই!');
        return;
      }
      const commData = commSnap.data();
      const alreadyMember = commData.adminUid === currentUid || commData.admins?.includes(currentUid) || commData.members?.includes(currentUid);
      if (!alreadyMember) {
        await updateDoc(commRef, { members: arrayUnion(currentUid) });
      }
      handleNavigate(`/community/${communityId}`, { state: { communityId } });
    } catch (error) {
      console.error('Join community failed:', error);
      showToast('জয়েন করতে সমস্যা হয়েছে!');
    }
  };

  useEffect(() => {
    const handleContextMenu = (e) => {
      if (e.target.closest('input, textarea, [contenteditable="true"]')) return;
      e.preventDefault();
    };
    document.addEventListener('contextmenu', handleContextMenu);
    return () => document.removeEventListener('contextmenu', handleContextMenu);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);

  const formatLastSeen = (tUser) => {
    if (!tUser) return 'Offline';
    if (tUser.isOnline) return 'online';
    if (tUser.role?.toLowerCase() === 'admin') return 'Offline';

    const lastSeen = tUser.lastSeen;
    if (!lastSeen) return 'Offline';

    let millis = null;
    if (typeof lastSeen === 'number') millis = lastSeen;
    else if (lastSeen.seconds) millis = lastSeen.seconds * 1000;
    else if (lastSeen.toDate) millis = lastSeen.toDate().getTime();

    if (!millis) return 'Offline';

    const diffMinutes = Math.floor((currentTime - millis) / 60000);

    if (diffMinutes < 1) return 'Active just now';
    if (diffMinutes < 60) return `Active ${diffMinutes}m ago`;

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `Active ${diffHours}h ago`;

    const dateObj = new Date(millis);
    const timeStr = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    
    const now = new Date(currentTime);
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);

    if (now.toDateString() === dateObj.toDateString()) {
      return `Active today at ${timeStr}`;
    } else if (yesterday.toDateString() === dateObj.toDateString()) {
      return `Active yesterday at ${timeStr}`;
    }
    return `Active ${dateObj.toLocaleDateString([], { month: 'short', day: 'numeric' })} at ${timeStr}`;
  };

  useEffect(() => {
    const userUid = localStorage.getItem('nexora_user_uid') || auth.currentUser?.uid;
    if (!userUid) { navigate('/login'); return; }
    setCurrentUid(userUid);

    const rId = getRoomId(userUid, targetUid);
    roomIdRef.current = rId;

    const savedMsgPins = JSON.parse(localStorage.getItem(`nexora_msg_pins_${rId}`) || '[]');
    setPinnedMsgIds(savedMsgPins);

    getDoc(doc(db, 'users', userUid)).then(snap => {
      if (snap.exists()) {
        const uData = snap.data();
        setCurrentUserData(uData);
        setUserProfilesMap(prev => ({ ...prev, [userUid]: uData }));
      }
    });

    if (userUid && targetUid && isWebrtcAvailable()) {
      attachRtcSignalListener();
      createRtcPeerConnection().catch(() => {});
    }

    const savedChats = getCachedData('nexora_dash_chats');
    const savedGroups = getCachedData('nexora_dash_groups');
    const savedComms = getCachedData('nexora_dash_comms');
    setForwardTargets([...savedChats, ...savedGroups, ...savedComms]);

    return () => {
      if (rtcSignalUnsubscribeRef.current) {
        rtcSignalUnsubscribeRef.current();
        rtcSignalUnsubscribeRef.current = null;
      }
      if (rtcPeerRef.current) {
        rtcPeerRef.current.close();
        rtcPeerRef.current = null;
      }
      if (rtcLocalAudioStreamRef.current) {
        rtcLocalAudioStreamRef.current.getTracks().forEach(track => track.stop());
        rtcLocalAudioStreamRef.current = null;
      }
    };

  }, [targetUid, navigate]);

  // Real-time Block Checking Logic
  useEffect(() => {
    if (!currentUserData?.phoneNumber || !targetUserData?.phoneNumber) return;

    const myPhone = normalizePhone(currentUserData.phoneNumber);
    const targetPhone = normalizePhone(targetUserData.phoneNumber);

    const iBlockedRef = doc(db, 'block_list', myPhone, 'blocked_users', targetPhone);
    const unsubIBlocked = onSnapshot(iBlockedRef, (snap) => {
      setHasBlockedTarget(snap.exists());
    });

    const targetBlockedRef = doc(db, 'block_list', targetPhone, 'blocked_users', myPhone);
    const unsubTargetBlocked = onSnapshot(targetBlockedRef, (snap) => {
      setIsBlockedByTarget(snap.exists());
    });

    return () => {
      unsubIBlocked();
      unsubTargetBlocked();
    };
  }, [currentUserData?.phoneNumber, targetUserData?.phoneNumber]);

  // Contact Reading Logic
  useEffect(() => {
    if (!currentUserData?.phoneNumber || !targetUserData?.phoneNumber) return;
    const myPhone = normalizePhone(currentUserData.phoneNumber);
    const targetPhone = normalizePhone(targetUserData.phoneNumber);

    const checkContactFromCacheOrFirestore = async () => {
      const localContacts = getLocalContacts();

      if (localContacts[targetPhone]) {
        setIsSavedContact(true);
        return;
      }

      try {
        const connSnap = await getDoc(doc(db, 'connections', myPhone));
        if (connSnap.exists()) {
          const connData = connSnap.data();
          const targetContactObj = connData.contacts?.[targetPhone];
          
          if (targetContactObj && targetContactObj.name) {
            saveLocalContact(targetPhone, targetContactObj.name);
            setIsSavedContact(true);
            return;
          }
        }
      } catch (err) {
        console.error("Error reading contact connection:", err);
      }

      setIsSavedContact(false);
    };

    checkContactFromCacheOrFirestore();
  }, [currentUserData?.phoneNumber, targetUserData?.phoneNumber]);

  const handleUnblockUser = async () => {
    if (!currentUserData?.phoneNumber || !targetUserData?.phoneNumber) return;
    const myPhone = normalizePhone(currentUserData.phoneNumber);
    const targetPhone = normalizePhone(targetUserData.phoneNumber);
    try {
      await deleteDoc(doc(db, 'block_list', myPhone, 'blocked_users', targetPhone));
      showToast("User unblocked!");
    } catch (e) {
      showToast("Failed to unblock user.");
    }
  };

  // Real-time Listener for Messages
  useEffect(() => {
    if (!currentUid || !targetUid || !roomIdRef.current) return;
    const rId = roomIdRef.current;

    updateDoc(doc(db, 'chats', rId), { [`unreadCount.${currentUid}`]: 0 }).catch(()=>{});

    const unsubscribeTargetUser = onSnapshot(doc(db, 'users', targetUid), (snap) => {
      if (snap.exists()) {
        const tData = snap.data();
        setTargetUserData(prev => ({ ...prev, ...tData }));
        setUserProfilesMap(prev => ({ ...prev, [targetUid]: tData }));
      }
    });

    const unsubscribeRoom = onSnapshot(doc(db, 'chats', rId), (roomSnap) => {
      if (roomSnap.exists()) {
        const roomData = roomSnap.data();
        setIsTargetTyping(roomData.typing && roomData.typing[targetUid]);
      }
    });

    const messagesRef = collection(db, 'chats', rId, 'messages');
    const q = query(messagesRef, orderBy('createdAt', 'desc'), limit(30));

    const unsubscribeMessages = onSnapshot(q, (snapshot) => {
      if (snapshot.empty) return;

      const fetched = [];
      snapshot.forEach((docSnap) => {
        fetched.push({ id: docSnap.id, ...docSnap.data() });
      });

      fetched.reverse();

      // IMPORTANT: capture the unread boundary BEFORE changing Firestore status.
      // Otherwise the first unread message can disappear on the next snapshot.
      const snapshotUnread = fetched
        .filter(msg => msg.senderId !== currentUid && msg.status !== 'seen')
        .sort((a, b) => {
          const at = getMessageMillis(a);
          const bt = getMessageMillis(b);
          if (at && bt && at !== bt) return at - bt;
          return 0;
        });

      if (!unreadAnchorInitializedRef.current && snapshotUnread.length > 0) {
        firstUnreadIdRef.current = snapshotUnread[0].id;
        totalUnreadCountRef.current = snapshotUnread.length;
        unreadAnchorInitializedRef.current = true;
      }

      const unreadIds = snapshotUnread.map(msg => msg.id);
      const unreadCount = unreadIds.length;

      // Mark them seen after the anchor has already been saved locally.
      snapshotUnread.forEach(msg => {
        updateDoc(doc(db, 'chats', rId, 'messages', msg.id), { status: 'seen' }).catch(()=>{});
      });

      setMessages(prev => {
        const map = new Map();
        prev.forEach(m => map.set(m.id, m));
        fetched.forEach(m => map.set(m.id, m));
        const updatedList = Array.from(map.values());

        // Preserve the Dashboard-captured unread metadata for the initial
        // render/position; after the user has opened the chat, clear it.
        saveChatCache(rId, updatedList, unreadIds, unreadCount);
        return updatedList;
      });
    });

    return () => {
      unsubscribeMessages();
      unsubscribeRoom();
      unsubscribeTargetUser();
    };
  }, [targetUid, currentUid]);

  // Load older messages when scrolling to top
  const loadMoreMessages = async () => {
    if(!hasMoreMsgs || isFetchingOld || messages.length === 0) return;
    setIsFetchingOld(true);
    
    try {
       const rId = roomIdRef.current;
       const messagesRef = collection(db, 'chats', rId, 'messages');
       
       const oldestMsg = messages[0];
       let oldestTimestamp = null;
       
       if (oldestMsg && oldestMsg.createdAt) {
         if (oldestMsg.createdAt instanceof Timestamp) {
           oldestTimestamp = oldestMsg.createdAt;
         } else if (typeof oldestMsg.createdAt.seconds === 'number') {
           oldestTimestamp = new Timestamp(oldestMsg.createdAt.seconds, oldestMsg.createdAt.nanoseconds || 0);
         } else if (typeof oldestMsg.createdAt === 'number') {
           oldestTimestamp = Timestamp.fromMillis(oldestMsg.createdAt);
         } else if (typeof oldestMsg.createdAt === 'string') {
           oldestTimestamp = Timestamp.fromDate(new Date(oldestMsg.createdAt));
         }
       }

       let q;
       if (lastVisibleMsg) {
         q = query(messagesRef, orderBy('createdAt', 'desc'), startAfter(lastVisibleMsg), limit(30));
       } else if (oldestTimestamp) {
         q = query(messagesRef, where('createdAt', '<', oldestTimestamp), orderBy('createdAt', 'desc'), limit(30));
       } else {
         setIsFetchingOld(false);
         return;
       }
       
       const querySnapshot = await getDocs(q);
       if(querySnapshot.empty) {
          setHasMoreMsgs(false);
          setIsFetchingOld(false);
          return;
       }

       const fetched = [];
       querySnapshot.forEach(docSnap => {
          fetched.push({ id: docSnap.id, ...docSnap.data() });
       });

       fetched.reverse();
       
       const prevScrollHeight = chatContainerRef.current ? chatContainerRef.current.scrollHeight : 0;
       
       setMessages(prev => {
         const map = new Map();
         fetched.forEach(m => map.set(m.id, m));
         prev.forEach(m => map.set(m.id, m));
         const merged = Array.from(map.values());

         const cached = getChatCache(rId);
         saveChatCache(rId, merged, cached.unreadIds, cached.unreadCount);
         return merged;
       });

       setLastVisibleMsg(querySnapshot.docs[querySnapshot.docs.length - 1]);
       if(querySnapshot.docs.length < 30) setHasMoreMsgs(false);

       setTimeout(() => {
          if(chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight - prevScrollHeight;
          }
       }, 50);

    } catch(e) {
      console.error("Error loading older messages:", e);
    }
    setIsFetchingOld(false);
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    try {
      if (roomIdRef.current) {
        localStorage.setItem(
          `nexora_p2p_scroll_${roomIdRef.current}`,
          String(Math.max(0, scrollTop))
        );
      }
    } catch (err) {}

    if(scrollTop === 0 && hasMoreMsgs && !isFetchingOld) {
        loadMoreMessages();
    }
    const isNotAtBottom = scrollHeight - scrollTop - clientHeight > 150;
    setShowScrollBottom(isNotAtBottom);
    if (!isNotAtBottom) {
      autoScrollLockedRef.current = false; 
    }
  };

  // Instant zero-delay unread scroll positioning before browser paint
  useLayoutEffect(() => {
    if (messages.length > 0 && chatContainerRef.current) {
      if (isInitialLoadRef.current) {
        // If Firestore has already marked statuses as seen, use the cached unread
        // IDs as a fallback so the initial position is still the same boundary.
        if (!firstUnreadIdRef.current && roomIdRef.current) {
          const cached = getChatCache(roomIdRef.current);
          const cachedUnreadIds = Array.isArray(cached.unreadIds) ? cached.unreadIds : [];
          const cachedUnreadInMessages = cachedUnreadIds
            .map(id => messages.find(m => m.id === id))
            .filter(Boolean)
            .sort((a, b) => getMessageMillis(a) - getMessageMillis(b));

          if (cachedUnreadInMessages.length > 0) {
            firstUnreadIdRef.current = cachedUnreadInMessages[0].id;
            totalUnreadCountRef.current = cached.unreadCount || cachedUnreadInMessages.length;
            unreadAnchorInitializedRef.current = true;
          }
        }

        const unreadEl = firstUnreadIdRef.current
          ? document.getElementById(`unread-divider-${firstUnreadIdRef.current}`)
          : null;

        if (firstUnreadIdRef.current && unreadEl) {
          // Put the unread divider exactly at the top boundary of the chat area.
          // This keeps the first unread message immediately below the divider,
          // while older messages remain available by scrolling upward.
          const container = chatContainerRef.current;
          const containerRect = container.getBoundingClientRect();
          const unreadRect = unreadEl.getBoundingClientRect();

          const targetScrollTop =
            container.scrollTop +
            (unreadRect.top - containerRect.top);

          container.scrollTop = Math.max(0, targetScrollTop);

          autoScrollLockedRef.current = true;
          setShowScrollBottom(true);
          isInitialLoadRef.current = false;
        } else if (!firstUnreadIdRef.current) {
          // No unread messages -> jump directly to bottom
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
          autoScrollLockedRef.current = false;
          setShowScrollBottom(false);
          isInitialLoadRef.current = false;
        }
      } else if (
        messagesEndRef.current &&
        !showScrollBottom &&
        !autoScrollLockedRef.current &&
        !isFetchingOld
      ) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [messages, isFetchingOld]);

  const handleTouchStartReact = (e, msg) => {
    if(selectedMessageIds.length > 0) return; 
    const touch = e.touches[0];
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
    const posY = touch.clientY; 
    
    msgLongPressTimerRef.current = setTimeout(() => {
      if (navigator.vibrate) navigator.vibrate(50);
      setReactionPopup({ msg, y: posY }); 
    }, 500);
  };

  const handleTouchMoveReact = (e, msg) => {
    if(selectedMessageIds.length > 0) return;
    const touch = e.touches[0];
    const diffX = touch.clientX - touchStartX.current;
    const diffY = Math.abs(touch.clientY - touchStartY.current);

    if (diffY > 15) {
      if (msgLongPressTimerRef.current) clearTimeout(msgLongPressTimerRef.current);
      return;
    }

    if (Math.abs(diffX) > 10) {
      if (msgLongPressTimerRef.current) clearTimeout(msgLongPressTimerRef.current);
      let limitedX = diffX;
      if (limitedX > 50) limitedX = 50;
      if (limitedX < -50) limitedX = -50;
      setSwipeX({ [msg.id]: limitedX });
    }
  };

  const handleTouchEndReact = (e, msg) => {
    if (msgLongPressTimerRef.current) clearTimeout(msgLongPressTimerRef.current);
    if(selectedMessageIds.length > 0) return;
    
    const diffX = swipeX[msg.id] || 0;
    if (Math.abs(diffX) >= 35) {
      if (navigator.vibrate) navigator.vibrate(50);
      setReplyingToMsg(msg); setEditingMsg(null);
    }
    setSwipeX({}); 
  };

  const toggleSelection = (msgId) => {
    if(selectedMessageIds.includes(msgId)) {
        setSelectedMessageIds(selectedMessageIds.filter(id => id !== msgId));
    } else {
        setSelectedMessageIds([...selectedMessageIds, msgId]);
    }
  };

  const handleMsgClick = (e, msg) => {
    e.stopPropagation();
    const id = msg.id;

    if (selectedMessageIds.length > 0) {
        toggleSelection(id);
        return;
    }

    clickCounts.current[id] = (clickCounts.current[id] || 0) + 1;
    if (clickCounts.current[id] === 3) {
      if (navigator.vibrate) navigator.vibrate(50);
      setMessageActionModal(msg);
      clickCounts.current[id] = 0;
    }
    clearTimeout(clickTimers.current[id]);
    clickTimers.current[id] = setTimeout(() => { clickCounts.current[id] = 0; }, 400);
  };

  // Reactions update with merge + instant local UI update
  const handleReactToPost = async (msgId, emoji) => {
    if (!roomIdRef.current || !currentUid) return;
    try {
      const rId = roomIdRef.current;
      const msgRef = doc(db, 'chats', rId, 'messages', msgId);
      
      await setDoc(msgRef, {
        reactions: {
          [currentUid]: emoji
        }
      }, { merge: true });

      setMessages(prev => {
        const updated = prev.map(m => {
          if (m.id === msgId) {
            const currentReactions = m.reactions || {};
            return {
              ...m,
              reactions: { ...currentReactions, [currentUid]: emoji }
            };
          }
          return m;
        });
        saveChatCache(rId, updated);
        return updated;
      });

      setReactionPopup(null);
    } catch (e) {
      console.error("Reaction failed:", e);
    }
  };

  const handlePhoneNumberClick = async (rawPhoneStr) => {
    let digitsOnly = rawPhoneStr.replace(/\D/g, '');
    if (digitsOnly.startsWith('880')) digitsOnly = digitsOnly.substring(3);
    if (digitsOnly.startsWith('0')) digitsOnly = digitsOnly.substring(1);
    const formattedPhone = `+880${digitsOnly}`;

    try {
      const userDocRef = doc(db, 'users', formattedPhone);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        setSelectedPhoneInfo({ phone: formattedPhone, exists: true, userData: userDocSnap.data() });
      } else {
        setSelectedPhoneInfo({ phone: formattedPhone, exists: false });
      }
    } catch (err) {
      setSelectedPhoneInfo({ phone: formattedPhone, exists: false });
    }
  };

  const handleLinkTouchStart = (e, textStr) => {
    e.stopPropagation();
    clickTimers.current['link'] = setTimeout(() => {
      if (navigator.vibrate) navigator.vibrate(50);
      navigator.clipboard.writeText(textStr);
      showToast("Copied!");
    }, 500);
  };

  const handleLinkTouchEnd = (e) => {
    e.stopPropagation();
    clearTimeout(clickTimers.current['link']);
  };

  const renderTextWithLinks = (textStr) => {
    if (!textStr) return '';
    return textStr.split('\n').map((line, lineIndex) => {
      const complexRegex = /(https?:\/\/[^\s]+|(?:www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\/[^\s]*)?|(?:\+880|880|0)1[3-9]\d{8}|\b\d+(?:[.,]\d+)*\b|@[a-zA-Z0-9_.-]+|@All Mention|@\w+\s\w+|\*\*.*?\*\*|\*[^*]+\*)/g;
      const parts = line.split(complexRegex);
      
      return (
        <React.Fragment key={lineIndex}>
          {parts.map((part, i) => {
            if (!part) return null;
            if (part.match(/^(?:\+880|880|0)1[3-9]\d{8}$/)) {
              return <span key={i} onTouchStart={(e) => handleLinkTouchStart(e, part)} onTouchEnd={handleLinkTouchEnd} onClick={(e) => { e.stopPropagation(); handlePhoneNumberClick(part); }} style={{ color: '#2563eb', fontWeight: '800', textDecoration: 'underline', cursor: 'pointer', pointerEvents: 'auto' }}>{part}</span>;
            } 
            else if (/^\d+(?:[.,]\d+)*$/.test(part)) {
              return <span key={i} onTouchStart={(e) => handleLinkTouchStart(e, part)} onTouchEnd={handleLinkTouchEnd} onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(part); showToast('Copied!'); }} style={{ color: '#2563eb', fontWeight: '800', textDecoration: 'underline', cursor: 'pointer', pointerEvents: 'auto' }}>{part}</span>;
            }
            else if (part.match(/^(https?:\/\/[^\s]+|(?:www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\/[^\s]*)?)$/)) {
              const href = part.startsWith('http') ? part : 'https://' + part;
              return <span key={i} onTouchStart={(e) => handleLinkTouchStart(e, part)} onTouchEnd={handleLinkTouchEnd} onClick={(e)=>{ e.stopPropagation(); window.open(href, '_blank'); }} style={{ color: '#2563eb', fontWeight: '800', textDecoration: 'underline', cursor: 'pointer', pointerEvents: 'auto' }}>{part}</span>;
            } 
            else if (part.match(/^@([a-zA-Z0-9_.-]+|All Mention|\w+\s\w+)$/)) {
              return (
                <span key={i} style={{ color: '#16a34a', fontWeight: '900', background: '#dcfce7', padding: '1px 6px', borderRadius: '6px' }}>
                  {part}
                </span>
              );
            } 
            else if (part.startsWith('**') && part.endsWith('**')) { return <strong key={i}>{part.slice(2, -2)}</strong>; } 
            else if (part.startsWith('*') && part.endsWith('*')) { return <strong key={i}>{part.slice(1, -1)}</strong>; }
            return <span key={i}>{part}</span>;
          })}
          <br />
        </React.Fragment>
      );
    });
  };

  const handleSaveContactLocal = async () => {
    if (!newContactName.trim() || !targetUserData?.phoneNumber || !currentUserData?.phoneNumber) return;
    
    const myPhone = normalizePhone(currentUserData.phoneNumber);
    const targetPhone = normalizePhone(targetUserData.phoneNumber);

    saveLocalContact(targetPhone, newContactName);
    setIsSavedContact(true);
    setShowAddContactModal(false);

    try {
      const connRef = doc(db, 'connections', myPhone);
      await setDoc(connRef, {
        contacts: {
          [targetPhone]: {
            name: newContactName,
            phoneNumber: targetPhone
          }
        }
      }, { merge: true });

      showToast(`${newContactName} নামে কন্টাক্ট সেভ হয়েছে!`);
    } catch (e) {
      console.error("Failed to save contact to connections:", e);
      showToast('কন্টাক্ট সেভ করতে সমস্যা হয়েছে!');
    }
  };

  // Delete message locally and from Firestore
  const handleDeleteMessage = async (msgId) => {
    if (await window.nexoraConfirm("মেসেজটি ডিলেট করতে চান?")) {
      try { 
        const roomId = roomIdRef.current;
        await deleteDoc(doc(db, 'chats', roomId, 'messages', msgId)); 
        
        setMessages(prev => {
          const updated = prev.filter(m => m.id !== msgId);
          saveChatCache(roomId, updated);
          return updated;
        });

        setFullScreenMedia(null); 
        setMessageActionModal(null); 
        setContextMsg(null); 
        showToast("Message deleted!");
      } catch(e){
        console.error("Delete failed:", e);
        showToast("Delete failed!");
      }
    }
  };

  const handleMultiDelete = async () => {
    if (await window.nexoraConfirm(`${selectedMessageIds.length} টি মেসেজ ডিলেট করতে চান?`)) {
        const roomId = roomIdRef.current;
        for(let id of selectedMessageIds) {
            const m = messages.find(x => x.id === id);
            if(m && m.senderId === currentUid) {
                await deleteDoc(doc(db, 'chats', roomId, 'messages', id)).catch(()=>{});
            }
        }
        setMessages(prev => {
          const updated = prev.filter(m => !selectedMessageIds.includes(m.id));
          saveChatCache(roomId, updated);
          return updated;
        });
        setSelectedMessageIds([]);
        showToast("Deleted!");
    }
  };

  const handleMultiCopy = () => {
    let combinedText = [];
    selectedMessageIds.forEach(id => {
        const m = messages.find(x => x.id === id);
        if(m && m.type === 'text' && m.text) combinedText.push(m.text);
    });
    if(combinedText.length > 0) {
        navigator.clipboard.writeText(combinedText.join('\n\n'));
        showToast("Copied!");
    }
    setSelectedMessageIds([]);
  };

  const handleMultiForward = () => {
    const msgsToFwd = selectedMessageIds.map(id => messages.find(x => x.id === id)).filter(x => x);
    setMsgToForward(msgsToFwd); 
    setShowForwardModal(true);
  };

  const getReplySummaryText = (msgObj) => {
      if (!msgObj) return null;
      if (msgObj.type === 'text') return msgObj.text;
      if (msgObj.type === 'voice') return '🎙️ Voice Message';
      if (msgObj.type === 'image') return '📷 Photo';
      if (msgObj.type === 'video') return '🎥 Video';
      if (msgObj.type === 'sticker') return '🎇 Sticker';
      if (msgObj.type === 'file') return '📄 File';
      if (msgObj.type === 'meet_card') return '📅 Meeting Card';
      if (msgObj.type === 'group_invite') return '💌 Group Invite';
      if (msgObj.type === 'community_invite') return '🔗 Community Invite';
      return 'Media Message';
  };

  const pushMessageToFirestore = async (type, payloadExtra, summaryText) => {
    if (!currentUid || !targetUid) return;
    if (hasBlockedTarget || isBlockedByTarget) {
      showToast("মেসেজ পাঠানো সম্ভব নয়।");
      return;
    }
    const roomId = getRoomId(currentUid, targetUid);
    const roomRef = doc(db, 'chats', roomId);

    setDoc(roomRef, { typing: { [currentUid]: false } }, { merge: true });

    try {
      const isTargetOnline = targetUserData?.isOnline === true;
      const initialStatus = isTargetOnline ? 'delivered' : 'sent';
      const replyTxt = replyingToMsg ? getReplySummaryText(replyingToMsg) : null;

      const msgData = {
        senderId: currentUid,
        senderName: currentUserData?.fullName || 'User',
        senderPhoto: currentUserData?.photoURL || '',
        type,
        replyToMsgId: replyingToMsg ? replyingToMsg.id : null,
        replyToMsgText: replyTxt,
        replyToSenderName: replyingToMsg ? replyingToMsg.senderName : null,
        status: initialStatus,
        createdAt: serverTimestamp(),
        timeStr: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }),
        ...payloadExtra
      };

      await addDoc(collection(db, 'chats', roomId, 'messages'), msgData);

      await updateDoc(roomRef, {
        roomId,
        participants: [currentUid, targetUid],
        lastMessage: summaryText,
        lastSenderId: currentUid,
        updatedAt: serverTimestamp(),
        [`unreadCount.${targetUid}`]: increment(1),
        [`unreadCount.${currentUid}`]: 0
      }).catch(async () => {
        await setDoc(roomRef, {
          roomId,
          participants: [currentUid, targetUid],
          lastMessage: summaryText,
          lastSenderId: currentUid,
          updatedAt: serverTimestamp(),
          unreadCount: { [targetUid]: 1, [currentUid]: 0 }
        }, { merge: true });
      });

      setReplyingToMsg(null);
    } catch (err) { } finally { setUploadProgress(0); }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (hasBlockedTarget || isBlockedByTarget) {
      showToast("মেসেজ পাঠানো সম্ভব নয়।");
      return;
    }
    if (!text.trim()) return;
    const msgText = text.trim();
    
    if (msgText === '@link') {
      setShowMeetForm(true); setText('');
      if (textareaRef.current) textareaRef.current.style.height = '20px'; 
      return;
    }
    if (msgText.startsWith('@@meet_card|')) {
      const parts = msgText.split('|');
      pushMessageToFirestore('meet_card', { companyName: parts[1], meetTime: parts[2], meetLink: parts[3] }, '📅 Live Meeting');
      setText('');
      if (textareaRef.current) textareaRef.current.style.height = '20px'; 
      return;
    }

    if (msgText.startsWith('@@community_card|')) {
      const parts = msgText.split('|');
      pushMessageToFirestore('community_invite', { communityName: parts[1], inviteCommunityId: parts[2] }, '🔗 Community Invite');
      setText('');
      if (textareaRef.current) textareaRef.current.style.height = '20px'; 
      return;
    }

    if (msgText.startsWith('@@group_invite|')) {
      const parts = msgText.split('|');

      pushMessageToFirestore(
        'group_invite',
        {
          inviteGroupId: parts[1],
          groupName: parts[2] || 'Group'
        },
        '💌 Group Invite'
      );

      setText('');
      if (textareaRef.current) textareaRef.current.style.height = '20px';
      return;
    }

    setText('');
    if (textareaRef.current) textareaRef.current.style.height = '20px'; 

    if (editingMsg) {
      try {
        await updateDoc(doc(db, 'chats', roomIdRef.current, 'messages', editingMsg.id), { text: msgText, isEdited: true });
        setEditingMsg(null);
      } catch (e) {}
    } else {
      const p2pMessageId = createRtcId('text');
      const p2pSent = sendWebrtcMessage(createP2PMessage('text', { id: p2pMessageId, text: msgText }));
      if (!p2pSent) {
        queuePendingWebrtcMessage(getRoomId(currentUid, targetUid), createP2PMessage('text', { id: p2pMessageId, text: msgText }));
      }
      pushMessageToFirestore('text', { text: msgText }, msgText);
    }
  };

  const handleSendMeetCard = () => {
    if (hasBlockedTarget || isBlockedByTarget) {
      showToast("মেসেজ পাঠানো সম্ভব নয়।");
      return;
    }
    if(!meetLinkInput) { return; }
    setShowMeetForm(false);
    pushMessageToFirestore('meet_card', { companyName: meetCompanyName, meetTime: meetTime, meetLink: meetLinkInput }, '📅 Live Meeting');
    setMeetLinkInput('');
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setText(val);

    if (textareaRef.current) {
      textareaRef.current.style.height = '20px';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }

    if (!currentUid || !targetUid) return;
    if (hasBlockedTarget || isBlockedByTarget) return;
    const roomId = getRoomId(currentUid, targetUid);
    const roomRef = doc(db, 'chats', roomId);
    setDoc(roomRef, { typing: { [currentUid]: true } }, { merge: true });

    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      setDoc(roomRef, { typing: { [currentUid]: false } }, { merge: true });
    }, 3000);
  };

  const handleMediaSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (hasBlockedTarget || isBlockedByTarget) {
      showToast("মিডিয়া পাঠানো সম্ভব নয়।");
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(1);

      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(-120);

      if (file.type.startsWith('video/')) {
        const storagePath = `chatVideos/${currentUid}/${targetUid}/${Date.now()}_${safeName}`;
        const fileRef = storageRef(storage, storagePath);

        const uploadTask = uploadBytesResumable(fileRef, file, {
          contentType: file.type || 'video/mp4',
          cacheControl: 'public,max-age=31536000'
        });

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            if (!snapshot.totalBytes) return;
            const progress = Math.floor((snapshot.bytesTransferred / snapshot.totalBytes) * 95);
            setUploadProgress(Math.min(95, progress));
          },
          (error) => {
            console.error('Video upload failed:', error);
            setUploadProgress(0);
            setIsUploading(false);
            showToast("ভিডিও পাঠানো যায়নি।");
          },
          async () => {
            try {
              setUploadProgress(97);
              const videoURL = await getDownloadURL(uploadTask.snapshot.ref);
              setUploadProgress(98);

              const p2pPayload = {
                id: createRtcId('video'),
                fileName: file.name,
                fileSize: file.size,
                mimeType: file.type || 'video/mp4',
                dataUrl: videoURL,
                url: videoURL,
                title: '🎥 Video'
              };
              sendP2PMedia('video', p2pPayload);

              await pushMessageToFirestore(
                'video',
                {
                  mediaData: videoURL,
                  mediaPath: storagePath,
                  fileName: file.name,
                  fileSize: file.size,
                  mimeType: file.type || 'video/mp4'
                },
                '🎥 Video'
              );

              setUploadProgress(100);
              setTimeout(() => { setUploadProgress(0); }, 500);

            } catch (error) {
              console.error('Video URL/message failed:', error);
              setUploadProgress(0);
              showToast("ভিডিও পাঠানো যায়নি।");
            } finally {
              setIsUploading(false);
            }
          }
        );

      } else {
        const base64Img = await compressImage(file);
        const blob = await (await fetch(base64Img)).blob();
        
        const storagePath = `chatImages/${currentUid}/${targetUid}/${Date.now()}_${safeName}`;
        const fileRef = storageRef(storage, storagePath);

        const uploadTask = uploadBytesResumable(fileRef, blob, {
          contentType: file.type || 'image/jpeg',
          cacheControl: 'public,max-age=31536000'
        });

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            if (!snapshot.totalBytes) return;
            const progress = Math.floor((snapshot.bytesTransferred / snapshot.totalBytes) * 95);
            setUploadProgress(Math.min(95, progress));
          },
          (error) => {
            console.error('Image upload failed:', error);
            setUploadProgress(0);
            setIsUploading(false);
            showToast("ছবি পাঠানো যায়নি।");
          },
          async () => {
            try {
              setUploadProgress(97);
              const imageURL = await getDownloadURL(uploadTask.snapshot.ref);
              setUploadProgress(98);

              const p2pPayload = {
                id: createRtcId('image'),
                fileName: file.name,
                fileSize: file.size,
                mimeType: file.type || 'image/jpeg',
                dataUrl: imageURL,
                url: imageURL,
                title: '📷 Photo'
              };
              sendP2PMedia('image', p2pPayload);

              await pushMessageToFirestore(
                'image',
                {
                  mediaData: imageURL,
                  mediaPath: storagePath,
                  fileName: file.name,
                  fileSize: file.size
                },
                '📷 Photo'
              );

              setUploadProgress(100);
              setTimeout(() => { setUploadProgress(0); }, 500);

            } catch (error) {
              console.error('Image URL/message failed:', error);
              setUploadProgress(0);
              showToast("ছবি পাঠানো যায়নি।");
            } finally {
              setIsUploading(false);
            }
          }
        );
      }

    } catch (error) {
      console.error('Media upload error:', error);
      setUploadProgress(0);
      setIsUploading(false);
      showToast("মিডিয়া পাঠানো যায়নি।");
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (hasBlockedTarget || isBlockedByTarget) {
      showToast("ফাইল পাঠানো সম্ভব নয়।");
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(1);

      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(-120);

      const storagePath = `chatFiles/${currentUid}/${targetUid}/${Date.now()}_${safeName}`;
      const fileRef = storageRef(storage, storagePath);

      const uploadTask = uploadBytesResumable(fileRef, file, {
        contentType: file.type || 'application/octet-stream',
        cacheControl: 'public,max-age=31536000'
      });

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          if (!snapshot.totalBytes) return;
          const progress = Math.floor((snapshot.bytesTransferred / snapshot.totalBytes) * 95);
          setUploadProgress(Math.min(95, progress));
        },
        (error) => {
          console.error('File upload failed:', error);
          setUploadProgress(0);
          setIsUploading(false);
          showToast("ফাইল পাঠানো যায়নি।");
        },
        async () => {
          try {
            setUploadProgress(97);
            const fileURL = await getDownloadURL(uploadTask.snapshot.ref);
            setUploadProgress(98);

            const p2pPayload = {
              id: createRtcId('file'),
              fileName: file.name,
              fileSize: file.size,
              mimeType: file.type || 'application/octet-stream',
              dataUrl: fileURL,
              url: fileURL,
              title: `📄 ${file.name}`
            };
            sendP2PMedia('file', p2pPayload);

            await pushMessageToFirestore(
              'file',
              {
                fileData: fileURL,
                fileURL: fileURL,
                mediaPath: storagePath,
                fileName: file.name,
                fileSize: file.size,
                mimeType: file.type || 'application/octet-stream'
              },
              `📄 ${file.name}`
            );

            setUploadProgress(100);
            setTimeout(() => { setUploadProgress(0); }, 500);

          } catch (error) {
            console.error('File URL/message failed:', error);
            setUploadProgress(0);
            showToast("ফাইল পাঠানো যায়নি।");
          } finally {
            setIsUploading(false);
          }
        }
      );

    } catch (error) {
      console.error('File upload error:', error);
      setUploadProgress(0);
      setIsUploading(false);
      showToast("ফাইল পাঠানো যায়নি।");
    }
  };

  const handlePaste = async (e) => {
    try {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();

          const file = item.getAsFile();
          if (!file) return;

          const input = document.createElement('input');
          input.type = 'file';
          input.files = (() => {
            const dt = new DataTransfer();
            dt.items.add(file);
            return dt.files;
          })();

          await handleMediaSelect({ target: input });
          return;
        }
      }
    } catch (error) {
      console.error('Paste error:', error);
    }
  };

  const handleCreateSticker = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploading(true);
    const compressedImg = await compressImage(file, true);
    const blob = await (await fetch(compressedImg)).blob();
    const newFile = new File([blob], 'sticker.png', { type: 'image/png' });
    const uploadedUrl = await uploadToImgBB(newFile);
    setIsUploading(false);
    if (uploadedUrl) {
      const newStickers = [uploadedUrl, ...savedStickers];
      setSavedStickers(newStickers);
      try{ localStorage.setItem('nexora_saved_stickers', JSON.stringify(newStickers)); }catch(e){}
      showToast("Sticker saved successfully!");
    }
  };

  const handleSendSticker = (url) => { setShowStickerModal(false); pushMessageToFirestore('sticker', { mediaData: url }, '🎇 Sticker'); };
  
  const saveSomeoneSticker = (url) => {
    if(!savedStickers.includes(url)) {
      const newStickers = [url, ...savedStickers];
      setSavedStickers(newStickers); 
      try{ localStorage.setItem('nexora_saved_stickers', JSON.stringify(newStickers)); }catch(e){}
      showToast("Sticker saved!");
    } else { showToast("Sticker already saved!"); }
    setMessageActionModal(null);
    setContextMsg(null);
  };
  
  const deleteSavedSticker = async (url) => {
    if (await window.nexoraConfirm("স্টিকারটি ডিলেট করতে চান?")) {
      const newStickers = savedStickers.filter(s => s !== url);
      setSavedStickers(newStickers); 
      try{ localStorage.setItem('nexora_saved_stickers', JSON.stringify(newStickers)); }catch(e){}
    }
  };

  const handleTogglePinMessage = async (msgId) => {
    const roomId = roomIdRef.current;
    let updated = [...pinnedMsgIds];

    if (updated.includes(msgId)) {
      updated = updated.filter(id => id !== msgId);
      setPinnedMsgIds(updated);
      try{ localStorage.setItem(`nexora_msg_pins_${roomId}`, JSON.stringify(updated)); }catch(e){}
      setMessageActionModal(null);
      setContextMsg(null);
    } else {
      if (updated.length >= 3) { showToast("Max 3 pinned messages allowed!"); return; }
      updated.push(msgId);
      setPinnedMsgIds(updated);
      try{ localStorage.setItem(`nexora_msg_pins_${roomId}`, JSON.stringify(updated)); }catch(e){}
      setMessageActionModal(null);
      setContextMsg(null);
      showToast("Message pinned!");
    }
  };

  const cyclePinnedMessage = (e) => {
      e.stopPropagation();
      if(pinnedMsgIds.length === 0) return;
      const nextIndex = (activePinIndex + 1) % pinnedMsgIds.length;
      setActivePinIndex(nextIndex);
      const targetMsgId = pinnedMsgIds[activePinIndex];
      
      const el = document.getElementById(`msg-${targetMsgId}`);
      if(el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setHighlightedMsgId(targetMsgId);
          setTimeout(() => setHighlightedMsgId(null), 2400); 
      }
  };

  const handleForwardMessageAction = async () => {
    if (selectedForwardIds.length === 0 || !msgToForward) return;
    setIsUploading(true);
    try {
      const msgsArray = Array.isArray(msgToForward) ? msgToForward : [msgToForward];

      for (let targetId of selectedForwardIds) {
        const target = forwardTargets.find(t => t.id === targetId);
        if (!target) continue;
        const path = target.isGroup ? 'groups' : target.isCommunity ? 'communities' : 'chats';
        
        for(let m of msgsArray) {
            const msgData = {
              senderId: currentUid, senderName: currentUserData?.fullName || 'User', senderPhoto: currentUserData?.photoURL || '',
              type: m.type, createdAt: serverTimestamp(), timeStr: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }),
              text: m.text || null, mediaData: m.mediaData || null, fileData: m.fileData || null, fileName: m.fileName || null, audioData: m.audioData || null, duration: m.duration || null, waveform: m.waveform || null,
              companyName: m.companyName || null, meetTime: m.meetTime || null, meetLink: m.meetLink || null, 
              inviteGroupId: m.inviteGroupId || null, groupName: m.groupName || null,
              inviteCommunityId: m.inviteCommunityId || null, communityName: m.communityName || null,
              isForwarded: true, status: 'sent'
            };
            await addDoc(collection(db, path, target.id, 'messages'), msgData);
        }
        await updateDoc(doc(db, path, target.id), { lastMessage: `${currentUserData?.fullName || 'User'}: Forwarded message(s)`, updatedAt: serverTimestamp() });
      }
      showToast("Forwarded successfully!");
      setShowForwardModal(false); setSelectedForwardIds([]); setMsgToForward(null); setMessageActionModal(null); setContextMsg(null);
      setSelectedMessageIds([]); 
    } catch (e) {} finally { setIsUploading(false); }
  };

  const getSafeAudioElement = (e) => {
    const audio = e?.currentTarget || e?.target || null;
    return audio && typeof audio === 'object' ? audio : null;
  };

  const getSafeAudioDuration = (audio) => {
    if (!audio) return 0;
    try {
      const value = Number(audio.duration);
      return Number.isFinite(value) && value >= 0 ? value : 0;
    } catch (_) {
      return 0;
    }
  };

  const getSafeAudioCurrentTime = (audio) => {
    if (!audio) return 0;
    try {
      const value = Number(audio.currentTime);
      return Number.isFinite(value) && value >= 0 ? value : 0;
    } catch (_) {
      return 0;
    }
  };

  const handleAudioPlay = (e) => {
    const current = getSafeAudioElement(e);
    if (!current) return;

    try {
      document.querySelectorAll('audio[data-nexora-voice]').forEach(a => {
        if (a !== current) {
          try { a.pause(); } catch (_) {}
        }
      });
    } catch (_) {}

    const id = typeof current.id === 'string' ? current.id.replace('voice-', '') : '';
    if (id) setPlayingVoiceId(id);
  };

  const handleVoiceLoadedMetadata = (e, id) => {
    if (!id) return;
    const audio = getSafeAudioElement(e);
    const duration = getSafeAudioDuration(audio);

    setVoicePlayback(prev => {
      const previous = prev?.[id] || {};
      return {
        ...prev,
        [id]: {
          ...previous,
          duration: duration > 0 ? duration : (Number(previous.duration) || 0),
          current: Number(previous.current) || 0
        }
      };
    });
  };

  const handleVoiceTimeUpdate = (e, id) => {
    if (!id) return;
    const audio = getSafeAudioElement(e);
    const current = getSafeAudioCurrentTime(audio);
    const duration = getSafeAudioDuration(audio);

    setVoicePlayback(prev => {
      const previous = prev?.[id] || {};
      return {
        ...prev,
        [id]: {
          ...previous,
          current,
          duration: duration > 0 ? duration : (Number(previous.duration) || 0)
        }
      };
    });
  };

  const handleVoiceEnded = (id) => {
    if (!id) return;
    setPlayingVoiceId(null);
    setVoicePlayback(prev => {
      const previous = prev?.[id] || {};
      const duration = Number(previous.duration) || 0;
      return {
        ...prev,
        [id]: {
          ...previous,
          current: duration,
          playing: false
        }
      };
    });
  };

  const toggleVoicePlayback = (id) => {
    if (!id) return;

    const audio = document.getElementById(`voice-${id}`);
    if (!audio || typeof audio.play !== 'function') return;

    try {
      document.querySelectorAll('audio[data-nexora-voice]').forEach(a => {
        if (a !== audio) {
          try { a.pause(); } catch (_) {}
        }
      });
    } catch (_) {}

    if (audio.paused) {
      Promise.resolve(audio.play())
        .then(() => setPlayingVoiceId(id))
        .catch(() => {});
    } else {
      try { audio.pause(); } catch (_) {}
      setPlayingVoiceId(null);
    }
  };

  const formatVoiceSeconds = (seconds) => {
    const s = Math.max(0, Math.floor(Number(seconds) || 0));
    return `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`;
  };

  // Mic preview stream & proper permission dialog
  const startMicPreviewStream = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        return null;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioCtx.createAnalyser();
      const source = audioCtx.createMediaStreamSource(stream);
      analyser.fftSize = 32; source.connect(analyser);
      audioContextRef.current = audioCtx; analyserRef.current = analyser;
      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const updateWave = () => {
        analyser.getByteFrequencyData(dataArray);
        const bars = Array.from(dataArray.slice(0, 10)).map(v => Math.max(10, v * 0.45));
        setWaveBars(bars);
        animFrameRef.current = requestAnimationFrame(updateWave);
      };
      updateWave(); return stream;
    } catch (e) { 
      console.warn("Mic preview stream request:", e);
      return null; 
    }
  };

  const stopMicPreviewStream = () => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') audioContextRef.current.close().catch(() => {});
  };

  const openVoiceModal = async () => { 
    if (hasBlockedTarget || isBlockedByTarget) {
      showToast("ভয়েস পাঠানো সম্ভব নয়।");
      return;
    }
    setShowVoiceModal(true); 
    setIsRecording(false); 
    setRecordingTime(0); 
    await startMicPreviewStream(); 
  };
  
  // Voice Recording with browser permission prompt
  const handleCircleVoiceClick = async () => {
    if (isRecording) { 
      stopAndSendVoice(); 
    } else {
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          showToast("Microphone not supported!");
          return;
        }

        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaRecorderRef.current = new MediaRecorder(stream);
        audioChunksRef.current = [];
        mediaRecorderRef.current.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
        
        mediaRecorderRef.current.onstop = async () => {
          stopMicPreviewStream();
          setIsUploading(true);
          setUploadProgress(1);

          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          const waveform = await buildRealWaveform(audioBlob);

          const storagePath = `chatVoice/${currentUid}/${targetUid}/${Date.now()}.webm`;
          const fileRef = storageRef(storage, storagePath);
          const uploadTask = uploadBytesResumable(fileRef, audioBlob, { contentType: 'audio/webm' });

          uploadTask.on(
            'state_changed',
            (snapshot) => {
              if (!snapshot.totalBytes) return;
              const progress = Math.floor((snapshot.bytesTransferred / snapshot.totalBytes) * 95);
              setUploadProgress(Math.min(95, progress));
            },
            (error) => {
              console.error('Voice upload failed:', error);
              setUploadProgress(0);
              setIsUploading(false);
              showToast("ভয়েস পাঠানো যায়নি।");
            },
            async () => {
              try {
                setUploadProgress(98);
                const voiceURL = await getDownloadURL(uploadTask.snapshot.ref);

                const p2pVoicePayload = {
                  id: createRtcId('voice'),
                  fileName: `voice-${Date.now()}.webm`,
                  duration: `${recordingTime}s`,
                  waveform,
                  dataUrl: voiceURL,
                  url: voiceURL,
                  title: '🎙️ Voice message'
                };
                sendP2PMedia('voice', p2pVoicePayload);

                await pushMessageToFirestore('voice', { 
                  audioData: voiceURL,
                  mediaPath: storagePath, 
                  duration: `${recordingTime}s`,
                  waveform
                }, '🎙️ Voice message');

                setUploadProgress(100);
                setTimeout(() => { setUploadProgress(0); }, 400);

              } catch (error) {
                console.error('Voice URL/message failed:', error);
                setUploadProgress(0);
                showToast("ভয়েস পাঠানো যায়নি।");
              } finally {
                setIsUploading(false);
              }
            }
          );

          stream.getTracks().forEach((t) => t.stop());
        };

        mediaRecorderRef.current.start(); 
        setIsRecording(true); 
        setRecordingTime(0);
        recordingIntervalRef.current = setInterval(() => { setRecordingTime((prev) => prev + 1); }, 1000);

      } catch (err) {
        console.error("Mic permission error:", err);
        showToast("Microphone permission denied!");
      }
    }
  };

  const stopAndSendVoice = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop(); setIsRecording(false); clearInterval(recordingIntervalRef.current); setShowVoiceModal(false);
    }
  };

  const cancelVoiceModal = () => {
    if (mediaRecorderRef.current && isRecording) { mediaRecorderRef.current.onstop = null; mediaRecorderRef.current.stop(); }
    stopMicPreviewStream(); clearInterval(recordingIntervalRef.current); setIsRecording(false); setShowVoiceModal(false);
  };

  const renderTickMarks = (msg) => {
    if (msg.senderId !== currentUid || msg.type === 'system') return null;

    if (msg.status === 'seen') {
      return <span style={{ color: '#62b875', fontSize: '10px', marginLeft: '4px' }}>🟢</span>;
    }
    if (msg.status === 'delivered') {
      return <span style={{ color: '#15803d', fontWeight: '900', marginLeft: '4px', fontSize: '12px' }}>✓✓</span>;
    }
    return <span style={{ color: '#ef4444', fontWeight: '900', marginLeft: '4px', fontSize: '12px' }}>✓</span>;
  };

  const formatDateBadge = (createdAt) => {
    if (!createdAt) return 'Today';
    let dateObj = createdAt.seconds ? new Date(createdAt.seconds * 1000) : new Date(createdAt);
    
    const now = new Date();
    if (now.toDateString() === dateObj.toDateString()) return 'Today';

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (yesterday.toDateString() === dateObj.toDateString()) return 'Yesterday';

    return dateObj.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isMsgEditable30s = (msg) => {
    if (!msg || msg.senderId !== currentUid || msg.type !== 'text') return false;
    let msgTimeMillis = Date.now();
    if (msg.createdAt?.seconds) msgTimeMillis = msg.createdAt.seconds * 1000;
    const diffSeconds = (Date.now() - msgTimeMillis) / 1000;
    return diffSeconds <= 30;
  };

  const handleSaveImage = (url) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = `nexora_media_${Date.now()}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const tRole = targetUserData?.role?.toLowerCase();
  const isTargetOwner = tRole === 'owner';
  const isTargetAdmin = tRole === 'admin';
  const isTargetPremium = tRole === 'premium';
  const isTargetSpecial = isTargetOwner || isTargetAdmin || isTargetPremium;
  const targetNameColor = isTargetAdmin ? '#15803d' : '#0f172a';

  const targetPhoneKey = normalizePhone(targetUserData?.phoneNumber);
  
  let displayName = targetUserData?.fullName || targetUserData?.phoneNumber || 'User';
  if (!isTargetSpecial && localContacts[targetPhoneKey]) {
      displayName = localContacts[targetPhoneKey];
  }

  const pinnedMessagesList = messages.filter(m => pinnedMsgIds.includes(m.id));
  let lastDateStr = '';

  return (
    <div style={styles.container}>
      <style>{`
        body, html, #root { margin: 0; padding: 0; height: 100dvh; width: 100vw; overflow: hidden; background: #fafafa; }
        * { box-sizing: border-box; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; -webkit-tap-highlight-color: transparent; -webkit-touch-callout: none; }
        
        .chat-card { 
          width: 100%; max-width: 480px; 
          height: 100dvh; max-height: 100dvh; 
          background: #fafafa; display: flex; flex-direction: column; position: relative; overflow: hidden; 
          box-shadow: 0 0 40px rgba(0,0,0,0.04); will-change: transform; 
        }
        @media (min-width: 768px) { .chat-card { height: 92vh; max-height: 92vh; border-radius: 36px; border: 1px solid #f1f5f9; } }
        @media (max-width: 600px) { .voice-message-card { width: 250px; } }
        
        .header-bar { background: #ffffff; padding: 12px 16px; padding-top: max(16px, env(safe-area-inset-top)); display: flex; justify-content: space-between; align-items: center; z-index: 30; border-bottom: 1px solid #f1f5f9; flex: 0 0 auto; }
        .multi-select-bar { background: #1e293b; padding: 12px 16px; padding-top: max(16px, env(safe-area-inset-top)); color: white; display: flex; justify-content: space-between; align-items: center; z-index: 100; flex: 0 0 auto; animation: slideDown 0.2s ease; }
        .pinned-msg-bar { background: #f0fdf4; border-bottom: 1px solid #bbf7d0; padding: 8px 16px; display: flex; align-items: center; justify-content: space-between; font-size: 13px; cursor: pointer; flex: 0 0 auto; }
        .add-contact-banner { background: #f0fdf4; border-bottom: 1px solid #bbf7d0; padding: 10px 16px; display: flex; align-items: center; justify-content: space-between; z-index: 15; flex: 0 0 auto; }
        
        .chat-messages-area { 
            flex: 1 1 auto; overflow-y: auto; overflow-x: hidden; padding: 12px; 
            display: flex; flex-direction: column; gap: 6px; min-height: 0; 
            -webkit-overflow-scrolling: touch; 
            overflow-anchor: none; 
        }

        .footer-container { flex: 0 0 auto; display: flex; flex-direction: column; background: #ffffff; z-index: 30; border-top: 1px solid #f1f5f9; }
        .chat-input-form { padding: 10px 12px; padding-bottom: max(12px, env(safe-area-inset-bottom)); display: flex; align-items: center; gap: 8px; }
        
        .msg-bubble-me { background: #62b875; color: #ffffff; border-radius: 18px 18px 2px 18px; padding: 8px 12px; max-width: 80%; align-self: flex-end; font-size: 14.5px; word-break: break-word; position: relative; user-select: none; -webkit-user-select: none; transition: transform 0.2s cubic-bezier(0.25, 1, 0.5, 1); }
        .msg-bubble-other { background: #ffffff; color: #0f172a; border-radius: 18px 18px 18px 2px; padding: 8px 12px; max-width: 80%; align-self: flex-start; font-size: 14.5px; word-break: break-word; border: 1px solid #f1f5f9; position: relative; user-select: none; -webkit-user-select: none; transition: transform 0.2s cubic-bezier(0.25, 1, 0.5, 1); }
        .msg-bubble-voice-me { align-self: flex-end; position: relative; user-select: none; -webkit-user-select: none; margin-bottom: 6px; transition: transform 0.2s cubic-bezier(0.25, 1, 0.5, 1); }
        .msg-bubble-voice-other { align-self: flex-start; position: relative; user-select: none; -webkit-user-select: none; margin-bottom: 6px; transition: transform 0.2s cubic-bezier(0.25, 1, 0.5, 1); }
        .msg-bubble-sticker { background: transparent; border: none; padding: 0; align-self: flex-start; transition: transform 0.2s cubic-bezier(0.25, 1, 0.5, 1); }
        .msg-bubble-sticker.is-me { align-self: flex-end; }

        @keyframes blinkGray { 0%, 100% { background-color: transparent; } 50% { background-color: rgba(148, 163, 184, 0.35); } }
        .msg-blink-container { animation: blinkGray 0.8s ease-in-out 3; border-radius: 12px; }

        .black-action-btn { width: 48px; height: 48px; border-radius: 50%; background: rgba(24, 24, 27, 0.8); color: white; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; }
        .add-btn { background: #62b875; color: white; border: none; padding: 6px 14px; border-radius: 14px; font-weight: 700; font-size: 12px; cursor: pointer; }

        .voice-message-card {
          width: 260px;
          min-height: 76px;
          display: flex;
          align-items: center;
          gap: 9px;
          padding: 8px 10px;
          border-radius: 18px;
        }
        .voice-message-card.voice-me { background: #62b875; color: #fff; }
        .voice-message-card.voice-other { background: #fff; color: #16a34a; border: 1px solid #e2e8f0; }
        .voice-play-btn {
          width: 38px;
          height: 38px;
          border: 0;
          border-radius: 50%;
          flex: 0 0 38px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 15px;
          cursor: pointer;
        }
        .voice-me .voice-play-btn { background: #fff; color: #62b875; }
        .voice-other .voice-play-btn { background: #22c55e; color: #fff; }
        .voice-wave-wrap { flex: 1; min-width: 0; }
        .voice-wave { height: 42px; display: flex; align-items: center; gap: 2px; overflow: hidden; }
        .voice-wave span { width: 3px; min-height: 5px; border-radius: 3px; transition: background .12s linear; }
        .voice-meta { display: flex; justify-content: space-between; font-size: 10px; opacity: .86; margin-top: 2px; }
        .voice-avatar { width: 34px; height: 34px; border-radius: 50%; object-fit: cover; flex: 0 0 34px; }

        .bottom-sheet-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.6); backdrop-filter: blur(4px); z-index: 100; display: flex; align-items: flex-end; justify-content: center; padding: 20px;}
        .voice-card-bottom { width: 100%; max-width: 480px; background: #ffffff; border-radius: 28px 28px 0 0; padding: 20px 24px 28px; box-shadow: 0 -10px 30px rgba(0,0,0,0.12); position: absolute; bottom: 0; text-align: center; border-top: 1px solid #f1f5f9; animation: slideUp 0.25s ease-out; }
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }

        .voice-circle { width: 96px; height: 96px; border-radius: 50%; background: #ffffff; border: 2px solid #62b875; display: flex; align-items: center; justify-content: center; margin: 12px auto 0; cursor: pointer; transition: all 0.2s; box-shadow: 0 8px 20px rgba(98, 184, 117, 0.1); }
        .voice-circle.recording { border-color: #ef4444; box-shadow: 0 8px 20px rgba(239, 68, 68, 0.2); animation: pulseRed 1.5s infinite; }
        @keyframes pulseRed { 0% { transform: scale(0.96); } 50% { transform: scale(1.04); } 100% { transform: scale(0.96); } }

        .date-badge { align-self: center; background: #ffffff; color: #94a3b8; font-size: 11px; font-weight: 600; padding: 4px 14px; border-radius: 16px; margin: 10px 0; border: 1px solid #f1f5f9; }
        .context-menu-card { width: 100%; max-width: 300px; background: #ffffff; border-radius: 20px; padding: 8px 0; box-shadow: 0 10px 30px rgba(0,0,0,0.15); border: 1px solid #f1f5f9; }
        .context-opt { padding: 12px 18px; font-size: 14px; font-weight: 600; color: #0f172a; display: flex; align-items: center; gap: 12px; cursor: pointer; }
        .context-opt:hover { background: #f8fafc; color: #62b875; }

        .phone-modal-card { width: 100%; max-width: 340px; background: white; border-radius: 24px; padding: 24px; text-align: center; box-shadow: 0 20px 40px rgba(0,0,0,0.15); }
        @keyframes slideDown { from { transform: translateY(-100%); } to { transform: translateY(0); } }

        .custom-toast { position: absolute; bottom: 90px; left: 50%; transform: translateX(-50%); background: rgba(15, 23, 42, 0.9); color: white; padding: 10px 20px; border-radius: 20px; font-size: 13px; font-weight: 600; z-index: 1000; animation: fadeInOut 2s ease forwards; pointer-events: none; box-shadow: 0 4px 15px rgba(0,0,0,0.2); white-space: nowrap; }
        @keyframes fadeInOut { 0% { opacity: 0; transform: translate(-50%, 15px); } 15% { opacity: 1; transform: translate(-50%, 0); } 85% { opacity: 1; transform: translate(-50%, 0); } 100% { opacity: 0; transform: translate(-50%, -15px); } }

        .anim-coin-flip { animation: coinFlip 4s ease-in-out infinite; transform-style: preserve-3d; }
        @keyframes coinFlip { 0%, 75% { transform: rotateY(0deg) scale(1); opacity: 1; } 85%, 90% { transform: rotateY(180deg) scale(0.5); opacity: 0; } 100% { transform: rotateY(360deg) scale(1); opacity: 1; } }
        .dynamic-shield { width: 16px; height: 16px; flex-shrink: 0; }
        .shield-color { animation: colorCycle 12s ease-in-out infinite alternate; }
        @keyframes colorCycle { 0%, 10% { fill: #22c55e; } 25%, 35% { fill: #eab308; } 50%, 60% { fill: #f97316; } 75%, 85% { fill: #ef4444; } 100% { fill: #3b82f6; } }

        .gold-name { font-size: inherit; font-weight: 900; background: linear-gradient(110deg, #B38728 0%, #B38728 40%, #FBF5B7 48%, #ffffff 50%, #FBF5B7 52%, #B38728 60%, #B38728 100%); background-size: 250% auto; color: transparent; -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; animation: textShine 2.5s linear infinite; letter-spacing: 0.5px; }
        @keyframes textShine { 0% { background-position: 200% center; } 100% { background-position: -100% center; } }
        .v-badge-3d { display: inline-flex; align-items: center; justify-content: center; width: 14px; height: 14px; background: linear-gradient(135deg, #FFDF00, #D4AF37); border-radius: 4px; color: #fff; text-shadow: 0px 1px 2px rgba(0,0,0,0.4); font-weight: 900; font-size: 9px; box-shadow: inset 1px 1px 2px rgba(255,255,255,0.9), inset -1px -1px 3px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.3); border: 1px solid #c9a42e; flex-shrink: 0; margin-left: 4px; }
        .blinking-owner { font-size: 11px; font-weight: 800; color: #d4af37; text-transform: uppercase; letter-spacing: 0.5px; text-shadow: 0 1px 2px rgba(0,0,0,0.15); animation: blinkGold 1.2s ease-in-out infinite; margin-left: 4px; }
        @keyframes blinkGold { 50% { opacity: 0.3; } }

        .img-3x4 { width: 100%; max-width: 250px; aspect-ratio: 3/4; object-fit: cover; border-radius: 12px; cursor: pointer; pointer-events: auto; }

        .magic-meet-wrapper { background: #ffffff; border-radius: 16px; border: 1px solid #e2e8f0; overflow: hidden; width: 280px; box-shadow: 0 10px 25px rgba(0,0,0,0.05); text-align: center; }
        .magic-meet-header { background: #f0fdf4; padding: 16px; border-bottom: 2px dashed #bbf7d0; }
        .magic-meet-title { font-size: 20px; font-weight: 900; color: #166534; margin: 0; letter-spacing: 0.5px; }
        .magic-meet-subtitle { font-size: 11px; color: #16a34a; font-weight: 700; background: #ffffff; padding: 4px 10px; border-radius: 20px; display: inline-block; margin-top: 8px; border: 1px solid #bbf7d0; }
        .magic-meet-timebox { display: flex; justify-around: space-around; padding: 12px; background: #ffffff; border-bottom: 1px solid #f1f5f9; }
        .magic-meet-timebox div { display: flex; flexDirection: column; align-items: center; font-size: 11px; color: #64748b; }
        .magic-meet-timebox b { font-size: 14px; color: #0f172a; margin-top: 2px; }
        .magic-meet-steps { padding: 12px 16px; text-align: left; font-size: 12px; color: #334155; font-weight: 600; background: #fafafa; }
        .magic-step-item { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
        .magic-step-no { background: #16a34a; color: white; border-radius: 50%; width: 18px; height: 18px; display: flex; align-items: center; justify-content: center; font-size: 10px; flex-shrink: 0; }
        .magic-join-btn { width: calc(100% - 24px); margin: 12px; padding: 12px; background: #16a34a; color: white; border: none; border-radius: 12px; font-weight: 900; font-size: 14px; cursor: pointer; box-shadow: 0 4px 15px rgba(22, 163, 74, 0.4); pointer-events: auto; }
        
        .reaction-global-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 100; }
        .floating-reaction-popup { position: fixed; background: #ffffff; padding: 4px 8px; border-radius: 30px; box-shadow: 0 10px 25px rgba(0,0,0,0.18); display: flex; gap: 4px; z-index: 101; border: 1px solid #f1f5f9; animation: popUp 0.18s ease-out; width: max-content; max-width: 90vw; overflow-x: auto; }
        @keyframes popUp { from { transform: translateX(-50%) scale(0.6); opacity: 0; } to { transform: translateX(-50%) scale(1); opacity: 1; } }
        .reaction-pop-emoji { background: none; border: none; font-size: 20px; cursor: pointer; transition: transform 0.15s ease; padding: 2px 4px; }
        .reaction-pop-emoji:active { transform: scale(1.3); }
      `}</style>

      {/* Custom Toast Notification */}
      {toastMsg && <div className="custom-toast">{toastMsg}</div>}

      <input type="file" ref={mediaInputRef} accept="image/*,video/*" onChange={handleMediaSelect} style={{ display: 'none' }} />
      <input type="file" ref={fileInputRef} accept="*" onChange={handleFileSelect} style={{ display: 'none' }} />
      <input type="file" ref={stickerImportRef} accept="image/*" onChange={handleCreateSticker} style={{ display: 'none' }} />

      {/* Magic Meet Form Modal */}
      {showMeetForm && (
        <div className="bottom-sheet-overlay" onClick={() => setShowMeetForm(false)} style={{ alignItems: 'center' }}>
          <div className="card-modal" onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: '28px', padding: '24px', width: '100%', maxWidth: '400px' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: '800', color: '#166534', textAlign: 'center' }}>📅 Create Magic Meet Card</h3>
            <input type="text" placeholder="কোম্পানির নাম (e.g., Selfgars)" value={meetCompanyName} onChange={(e)=>setMeetCompanyName(e.target.value)} style={{ width: '100%', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '12px', marginBottom: '10px', outline: 'none' }} />
            <input type="text" placeholder="মিটিং সময় (e.g., ২:৩০)" value={meetTime} onChange={(e)=>setMeetTime(e.target.value)} style={{ width: '100%', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '12px', marginBottom: '10px', outline: 'none' }} />
            <input type="url" placeholder="Google Meet Link (https://meet...)" value={meetLinkInput} onChange={(e)=>setMeetLinkInput(e.target.value)} style={{ width: '100%', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '12px', marginBottom: '16px', outline: 'none' }} />
            <button onClick={handleSendMeetCard} style={{ width: '100%', padding: '14px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '800', cursor: 'pointer' }}>Generate & Send</button>
          </div>
        </div>
      )}

      {/* Sticker Bottom Sheet */}
      {showStickerModal && (
        <div className="bottom-sheet-overlay" style={{ alignItems: 'flex-end', padding: 0 }}>
          <div className="voice-card-bottom" style={{ height: '350px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexShrink: 0 }}>
              <span style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>Stickers</span>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button onClick={() => stickerImportRef.current?.click()} style={{ background: '#16a34a', color: '#ffffff', border: 'none', padding: '6px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '800', cursor: 'pointer', boxShadow: '0 4px 10px rgba(22, 163, 74, 0.3)' }}>
                  {isUploading ? 'Creating...' : '➕ Create Sticker'}
                </button>
                <button onClick={() => setShowStickerModal(false)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer' }}>✕</button>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center', paddingBottom: '20px' }}>
              {savedStickers.length === 0 && <p style={{ color: '#64748b', fontSize: '13px' }}>No stickers saved. Click Create to make one!</p>}
              {savedStickers.map((url, i) => (
                <img 
                  key={i} 
                  src={url} 
                  onClick={() => handleSendSticker(url)} 
                  onContextMenu={(e) => { e.preventDefault(); deleteSavedSticker(url); }}
                  onTouchStart={(e) => {
                    stickerLongPressTimerRef.current = setTimeout(() => {
                      if(navigator.vibrate) navigator.vibrate(50);
                      deleteSavedSticker(url);
                    }, 600);
                  }}
                  onTouchEnd={() => clearTimeout(stickerLongPressTimerRef.current)}
                  alt="Sticker" 
                  style={{ width: '70px', height: '70px', cursor: 'pointer', border: '1px solid #f1f5f9', borderRadius: '8px', padding: '4px' }} 
                />
              ))}
            </div>
            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '10px' }}>Tap to send. Long press to delete.</div>
          </div>
        </div>
      )}

      {/* মোবাইল নম্বর টাচ মডাল */}
      {selectedPhoneInfo && (
        <div className="bottom-sheet-overlay" style={{ alignItems: 'center' }}>
          <div className="phone-modal-card">
            <h3 style={{ fontSize: '18px', fontWeight: '800', margin: '0 0 6px', color: '#0f172a' }}>{selectedPhoneInfo.phone}</h3>
            
            {selectedPhoneInfo.exists ? (
              <div>
                <p style={{ fontSize: '13px', color: '#62b875', fontWeight: '700', marginBottom: '16px' }}>
                  ✓ This number is registered on Nexora ({selectedPhoneInfo.userData?.fullName})
                </p>
                <button 
                  onClick={() => {
                    const tId = selectedPhoneInfo.userData?.uid;
                    setSelectedPhoneInfo(null);
                    handleNavigate(`/chat/${tId}`);
                  }}
                  style={{ width: '100%', padding: '12px', background: '#62b875', color: 'white', border: 'none', borderRadius: '14px', fontWeight: '700', cursor: 'pointer', marginBottom: '8px' }}
                >
                  💬 Message on Nexora
                </button>
              </div>
            ) : (
              <div>
                <p style={{ fontSize: '13px', color: '#ef4444', fontWeight: '600', marginBottom: '16px' }}>
                  This number is not registered on Nexora.
                </p>
                <a 
                  href={`tel:${selectedPhoneInfo.phone}`}
                  style={{ display: 'block', width: '100%', padding: '12px', background: '#0f172a', color: 'white', textDecoration: 'none', borderRadius: '14px', fontWeight: '700', marginBottom: '8px' }}
                >
                  📞 Dial on Call
                </a>
              </div>
            )}

            <button onClick={() => setSelectedPhoneInfo(null)} style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '13px', fontWeight: '600', cursor: 'pointer', marginTop: '6px' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Full Screen Media Viewer */}
      {fullScreenMedia && (
        <div style={{ position: 'absolute', top:0, left:0, right:0, bottom:0, background: 'rgba(0,0,0,0.95)', zIndex: 400, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button onClick={() => setFullScreenMedia(null)} style={{ background: 'none', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer' }}>✕</button>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              <button onClick={() => { setMsgToForward([fullScreenMedia]); setShowForwardModal(true); setFullScreenMedia(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2"><polyline points="15 14 20 9 15 4"/><path d="M4 20v-7a4 4 0 0 1 4-4h12"/></svg>
              </button>
              <button onClick={() => handleSaveImage(fullScreenMedia.mediaData)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
              </button>
              {(fullScreenMedia.senderId === currentUid) && (
                <button onClick={() => handleDeleteMessage(fullScreenMedia.id)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                </button>
              )}
            </div>
          </div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px' }}>
            {fullScreenMedia.type === 'video' ? (
              <video src={fullScreenMedia.mediaData} controls autoPlay style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            ) : (
              <img src={fullScreenMedia.mediaData} alt="Full" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            )}
          </div>
        </div>
      )}

      {/* Message Details Modal */}
      {msgDetails && (
        <div className="bottom-sheet-overlay" onClick={() => setMsgDetails(null)}>
          <div className="voice-card-bottom" onClick={e => e.stopPropagation()} style={{ borderRadius: '28px', padding: '24px' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>Message Details</h3>
            <div style={{ textAlign: 'left', fontSize: '14px', color: '#334155', lineHeight: '1.8' }}>
              <p><strong>Sent by:</strong> {msgDetails.senderName || 'You'}</p>
              <p><strong>Time:</strong> {msgDetails.timeStr || new Date(msgDetails.createdAt?.seconds * 1000).toLocaleString()}</p>
              <p><strong>Status:</strong> {msgDetails.status === 'seen' ? '✅ Read (Seen)' : msgDetails.status === 'delivered' ? '✓✓ Delivered' : '✓ Sent'}</p>
              {msgDetails.type === 'voice' && msgDetails.listened && (
                 <p><strong>Listened:</strong> {msgDetails.listened}</p>
              )}
            </div>
            <button onClick={() => setMsgDetails(null)} style={{ width: '100%', padding: '12px', background: '#f1f5f9', border: 'none', borderRadius: '12px', fontWeight: '800', marginTop: '16px', cursor: 'pointer' }}>Close</button>
          </div>
        </div>
      )}

      {/* Action Menu Modal (Triple Click) */}
      {messageActionModal && (
        <div className="bottom-sheet-overlay" onClick={() => setMessageActionModal(null)}>
          <div className="context-menu-card" onClick={e => e.stopPropagation()} style={{ padding: '12px 0' }}>
            
            <div className="context-opt" onClick={() => { setSelectedMessageIds([messageActionModal.id]); setMessageActionModal(null); }}>
              <span>☑️</span> <span>Select Message</span>
            </div>

            {messageActionModal.senderId === currentUid && (
              <div className="context-opt" onClick={() => { setMsgDetails(messageActionModal); setMessageActionModal(null); }}>
                <span>ℹ️</span> <span>Message Details</span>
              </div>
            )}
            
            {messageActionModal.text && (
              <div className="context-opt" onClick={() => { navigator.clipboard.writeText(messageActionModal.text); setMessageActionModal(null); showToast("Copied!"); }}>
                <span>📋</span> <span>Copy Text</span>
              </div>
            )}
            
            {messageActionModal.type === 'meet_card' && (
              <div className="context-opt" onClick={() => { navigator.clipboard.writeText(`@@meet_card|${messageActionModal.companyName}|${messageActionModal.meetTime}|${messageActionModal.meetLink}`); setMessageActionModal(null); showToast("Meeting Card Copied!"); }}>
                <span>📋</span> <span>Copy Meeting Card</span>
              </div>
            )}

            {messageActionModal.type === 'community_invite' && (
              <div className="context-opt" onClick={() => { 
                navigator.clipboard.writeText(`@@community_card|${messageActionModal.communityName}|${messageActionModal.inviteCommunityId}`); 
                setMessageActionModal(null); 
                showToast("Community Link Copied!"); 
              }}>
                <span>📋</span> <span>Copy Invite Card</span>
              </div>
            )}

            {messageActionModal.type === 'sticker' && (
              <div className="context-opt" onClick={() => saveSomeoneSticker(messageActionModal.mediaData)}>
                <span>⭐</span> <span>Save Sticker</span>
              </div>
            )}

            <div className="context-opt" onClick={() => { setMsgToForward([messageActionModal]); setShowForwardModal(true); setMessageActionModal(null); }}>
              <span>↪️</span> <span>Forward</span>
            </div>

            {pinnedMsgIds.includes(messageActionModal.id) ? (
                <div className="context-opt" onClick={() => handleTogglePinMessage(messageActionModal.id)}>
                  <span style={{color: '#ef4444'}}>📌</span> <span style={{color: '#ef4444'}}>Unpin Message</span>
                </div>
            ) : (
                <div className="context-opt" onClick={() => handleTogglePinMessage(messageActionModal.id)}>
                  <span>📌</span> <span>Pin Message</span>
                </div>
            )}

            {isMsgEditable30s(messageActionModal) && (
              <div className="context-opt" onClick={() => { setEditingMsg(messageActionModal); setEditInputText(messageActionModal.text); setMessageActionModal(null); }}>
                <span>✏️</span> <span>Edit Message</span>
              </div>
            )}

            {messageActionModal.senderId === currentUid && (
              <div className="context-opt" style={{ color: '#ef4444' }} onClick={() => { handleDeleteMessage(messageActionModal.id); setMessageActionModal(null); }}>
                <span>🗑️</span> <span>Delete Message</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Forward Modal */}
      {showForwardModal && (
        <div className="bottom-sheet-overlay" onClick={() => { setShowForwardModal(false); setSelectedForwardIds([]); setMsgToForward(null); }}>
          <div className="voice-card-bottom" onClick={e => e.stopPropagation()} style={{ borderRadius: '28px', padding: '16px', maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>Forward to...</span>
              <button onClick={() => { setShowForwardModal(false); setSelectedForwardIds([]); setMsgToForward(null); }} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer' }}>✕</button>
            </div>
            
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {forwardTargets.map(t => {
                const isSelected = selectedForwardIds.includes(t.id);
                
                let finalName = t.title || t.name || t.fullName || t.displayName || t.chatName;
                let finalPhoto = t.photoURL || t.groupImage || t.photo || t.avatar;

                if (!t.isGroup && !t.isCommunity) {
                    const pList = t.participants || t.users || [];
                    let fwTargetUid = t.otherUserId || t.uid || t.participantId || t.userId;
                    
                    if (!fwTargetUid && pList.length > 0) fwTargetUid = pList.find(p => p !== currentUid);
                    if (!fwTargetUid && t.id && typeof t.id === 'string' && t.id.includes('_')) {
                        fwTargetUid = t.id.split('_').find(p => p !== currentUid);
                    }
                    if (!fwTargetUid) fwTargetUid = t.id;

                    const matchedProfile = userProfilesMap[fwTargetUid];

                    if (matchedProfile) {
                        finalPhoto = matchedProfile.photoURL || finalPhoto;
                        finalName = matchedProfile.fullName || matchedProfile.name || finalName;
                        if (matchedProfile.phoneNumber) {
                            const normP = normalizePhone(matchedProfile.phoneNumber);
                            if (localContacts[normP]) finalName = localContacts[normP];
                        }
                    } else if (fwTargetUid) {
                        const normP = normalizePhone(fwTargetUid);
                        if (localContacts[normP]) finalName = localContacts[normP];
                    }
                    finalName = finalName || 'User';
                } else {
                    finalName = finalName || 'Group/Community';
                }

                finalPhoto = finalPhoto || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150";

                return (
                  <div key={t.id} onClick={() => {
                    if(isSelected) setSelectedForwardIds(selectedForwardIds.filter(id => id !== t.id));
                    else setSelectedForwardIds([...selectedForwardIds, t.id]);
                  }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px', borderBottom: '1px solid #e2e8f0', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <img src={finalPhoto} alt="Target" style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover' }} />
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '14px' }}>{finalName}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{t.isGroup ? 'Group' : t.isCommunity ? 'Community' : 'Chat'}</div>
                      </div>
                    </div>
                    <div className={`custom-checkbox ${isSelected ? 'checked' : ''}`}>{isSelected && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}</div>
                  </div>
                );
              })}
            </div>
            {selectedForwardIds.length > 0 && (
              <button onClick={handleForwardMessageAction} disabled={isUploading} style={{ width: '100%', padding: '14px', background: '#62b875', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '800', marginTop: '10px' }}>
                {isUploading ? 'Forwarding...' : `Forward to ${selectedForwardIds.length} chats`}
              </button>
            )}
          </div>
        </div>
      )}

      {/* এডিট মডাল */}
      {editingMsg && (
        <div className="bottom-sheet-overlay" style={{ alignItems: 'center' }}>
          <div className="voice-card-bottom" style={{ borderRadius: '28px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '800', margin: '0 0 12px', color: '#0f172a' }}>Edit Message</h3>
            <input 
              type="text" 
              value={editInputText} 
              onChange={(e) => setEditInputText(e.target.value)}
              style={{ width: '100%', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '14px', fontSize: '14px', outline: 'none', marginBottom: '14px' }}
            />
            <button onClick={() => {
                if(!editInputText.trim()) return;
                updateDoc(doc(db, 'chats', roomIdRef.current, 'messages', editingMsg.id), { text: editInputText, isEdited: true });
                setEditingMsg(null); setContextMsg(null); setEditInputText('');
            }} style={{ width: '100%', padding: '12px', background: '#62b875', color: 'white', border: 'none', borderRadius: '14px', fontWeight: '700', cursor: 'pointer' }}>
              Save Edit
            </button>
            <button onClick={() => setEditingMsg(null)} style={{ background: 'none', border: 'none', color: '#64748b', marginTop: '10px', cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}

      {/* ভয়েস রেকর্ডার */}
      {showVoiceModal && (
        <div className="bottom-sheet-overlay">
          <div className="voice-card-bottom">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>Voice Note</span>
              <button onClick={cancelVoiceModal} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#64748b', padding: '0 4px' }}>✕</button>
            </div>

            <div className={`voice-circle ${isRecording ? 'recording' : ''}`} onClick={handleCircleVoiceClick}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', height: '36px' }}>
                {waveBars.map((val, idx) => (
                  <div key={idx} style={{ width: '3px', height: `${val}px`, backgroundColor: isRecording ? '#ef4444' : '#62b875', borderRadius: '3px', transition: 'height 0.05s ease' }} />
                ))}
              </div>
            </div>

            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '15px', fontWeight: '800', color: isRecording ? '#ef4444' : '#0f172a' }}>
                {isRecording ? `Recording... (${recordingTime}s)` : 'Tap on start record'}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
              <button onClick={cancelVoiceModal} style={{ flex: 1, padding: '10px', background: '#f1f5f9', border: 'none', borderRadius: '12px', fontWeight: '700', color: '#64748b', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
              <button onClick={() => { cancelVoiceModal(); setTimeout(openVoiceModal, 200); }} style={{ flex: 1, padding: '10px', background: '#e2f4e6', border: 'none', borderRadius: '12px', fontWeight: '700', color: '#15803d', cursor: 'pointer', fontSize: '12px' }}>Re-record</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Contact মডাল */}
      {showAddContactModal && (
        <div className="bottom-sheet-overlay" style={{ alignItems: 'center' }}>
          <div className="voice-card-bottom" style={{ borderRadius: '28px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '800', margin: '0 0 6px', color: '#0f172a' }}>Save to Contacts</h3>
            <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '14px' }}>{targetUserData?.phoneNumber}</p>
            <input 
              type="text" 
              placeholder="Enter Contact Name"
              value={newContactName}
              onChange={(e) => setNewContactName(e.target.value)}
              style={{ width: '100%', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '14px', fontSize: '14px', marginBottom: '14px', outline: 'none' }}
            />
            <button onClick={handleSaveContactLocal} style={{ width: '100%', padding: '12px', background: '#62b875', color: 'white', border: 'none', borderRadius: '14px', fontWeight: '700', cursor: 'pointer' }}>
              Save Contact
            </button>
            <button onClick={() => setShowAddContactModal(false)} style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '13px', fontWeight: '600', cursor: 'pointer', marginTop: '10px' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* মেইন চ্যাট স্ক্রিন */}
      <div className="chat-card">
        
        {/* ১. হেডার এরিয়া */}
        {selectedMessageIds.length > 0 ? (
          <div className="multi-select-bar">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button onClick={() => { setSelectedMessageIds([]); }} style={{ background: 'none', border: 'none', color: 'white', fontSize: '18px', cursor: 'pointer' }}>✕</button>
              <span style={{ fontWeight: '800' }}>{selectedMessageIds.length} Selected</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <button onClick={handleMultiCopy} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }} title="Copy">
                 <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
              </button>
              <button onClick={handleMultiForward} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }} title="Forward">
                 <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 14 20 9 15 4"/><path d="M4 20v-7a4 4 0 0 1 4-4h12"/></svg>
              </button>
              <button onClick={handleMultiDelete} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#ef4444' }}>🗑️</button>
            </div>
          </div>
        ) : (
          <div 
            className="header-bar" 
            onClick={() => handleNavigate(`/profile-info/${targetUid}`)} 
            style={{ cursor: 'pointer' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button onClick={(e) => { e.stopPropagation(); handleNavigate('/dashboard'); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
              </button>
              
              <div className={isTargetSpecial ? 'anim-coin-flip' : ''} style={{ width: '40px', height: '40px', perspective: '800px', flexShrink: 0 }}>
                <img 
                  src={targetUserData?.photoURL || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150"} 
                  alt="User" 
                  style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} 
                />
              </div>
              
              <div>
                <h3 style={{ fontSize: '15px', fontWeight: '700', margin: 0, color: targetNameColor, display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {isTargetOwner ? <span className="gold-name">{displayName}</span> : displayName}
                  {isTargetOwner && <div className="v-badge-3d">V</div>}
                  {(isTargetAdmin || isTargetPremium) && (
                    <svg className="dynamic-shield" viewBox="0 0 100 100">
                      <path className="shield-color" d="M50 5 L90 20 L90 50 C90 75 50 95 50 95 C50 95 10 75 10 50 L10 20 Z"/>
                      <path d="M50 11 L84 24 L84 49 C84 70 50 87 50 87 C50 87 16 70 16 49 L16 24 Z" fill="none" stroke="white" strokeWidth="2.5" opacity="0.8"/>
                      <path d="M32 48 L45 61 L68 38" stroke="white" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </h3>
                
                {isTargetTyping ? (
                  <span style={{ fontSize: '11px', color: '#62b875', fontWeight: '700' }}>typing...</span>
                ) : targetUserData?.isOnline ? (
                  <span style={{ fontSize: '11px', color: '#62b875', fontWeight: '600' }}>online</span>
                ) : (
                  <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '500' }}>
                    {formatLastSeen(targetUserData)}
                  </span>
                )}
                {isTargetOwner && <span className="blinking-owner" style={{marginLeft: '6px'}}>Owner</span>}
              </div>
            </div>

            {!hasBlockedTarget && !isBlockedByTarget && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button onClick={(e) => { e.stopPropagation(); handleNavigate('/call', { state: { targetUid, callType: 'video' } }); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="3"/></svg>
                </button>

                <button onClick={(e) => { e.stopPropagation(); handleNavigate('/call', { state: { targetUid, callType: 'audio' } }); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.79 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                </button>
              </div>
            )}
          </div>
        )}

        {/* ২. পিনড মেসেজ ব্যানার */}
        {pinnedMessagesList.length > 0 && (
          <div className="pinned-msg-bar" onClick={cyclePinnedMessage}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden', whiteSpace: 'nowrap' }}>
              <span style={{ fontSize: '16px' }}>📌</span>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontWeight: '800', color: '#16a34a' }}>
                  Pinned Message {pinnedMessagesList.length > 1 ? `(${activePinIndex + 1}/${pinnedMessagesList.length})` : ''}
                </span>
                <span style={{ color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {pinnedMessagesList[activePinIndex]?.type === 'text' 
                      ? pinnedMessagesList[activePinIndex].text 
                      : `[${(pinnedMessagesList[activePinIndex]?.type || 'Media').toUpperCase()}]`}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* অপরিচিত কন্টাক্ট সেভ ব্যানার */}
        {!isSavedContact && targetUserData && (
          <div className="add-contact-banner">
            <span style={{ fontSize: '12px', color: '#15803d', fontWeight: '600' }}>
              This sender is not in your phone contacts.
            </span>
            <button onClick={() => setShowAddContactModal(true)} className="add-btn">
              + Add to Contact
            </button>
          </div>
        )}

        {/* প্রোগ্রেস বার */}
        {uploadProgress > 0 && (
          <div style={{ padding: '8px 16px', background: '#f0fdf4', flexShrink: 0 }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: '#62b875' }}>Sending File ({uploadProgress}%)...</span>
          </div>
        )}

        {/* ৩. স্ক্রলেবল মেসেজ ফিড এরিয়া */}
        <div className="chat-messages-area" ref={chatContainerRef} onScroll={handleScroll}>
          {isFetchingOld && (
            <div style={{ textAlign: 'center', padding: '10px', fontSize: '12px', color: '#64748b', fontWeight: '600' }}>
               Loading older messages...
            </div>
          )}

          {/* রিয়েকশন ওভারলে */}
          {reactionPopup && (
            <div className="reaction-global-overlay" onClick={() => { setReactionPopup(null); setCustomEmojiMsgId(null); }}>
              <div 
                className="floating-reaction-popup" 
                style={{ top: reactionPopup.y > 100 ? reactionPopup.y - 70 : 80, left: '50%', transform: 'translateX(-50%)' }} 
                onClick={(e) => e.stopPropagation()}
              >
                {['👍', '❤️', '😂', '😮', '😢', '🙏'].map(emoji => (
                  <button key={emoji} onClick={() => handleReactToPost(reactionPopup.msg.id, emoji)} className="reaction-pop-emoji">{emoji}</button>
                ))}
                {customEmojiMsgId === reactionPopup.msg.id ? (
                  <input autoFocus type="text" placeholder="😀" onChange={(e) => { if(e.target.value) { handleReactToPost(reactionPopup.msg.id, e.target.value); setCustomEmojiMsgId(null); } }} onBlur={() => setCustomEmojiMsgId(null)} style={{ width: '28px', fontSize: '16px', background: 'transparent', border: 'none', outline: 'none', textAlign: 'center', padding: 0 }} />
                ) : (
                  <button onClick={(e) => { e.stopPropagation(); setCustomEmojiMsgId(reactionPopup.msg.id); }} className="reaction-pop-emoji" style={{ background: '#f1f5f9', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', marginLeft: '4px' }}>➕</button>
                )}
              </div>
            </div>
          )}

          {messages.map((msg) => {
            const isMe = msg.senderId === currentUid;
            const isSelected = selectedMessageIds.includes(msg.id);
            const isHighlighted = highlightedMsgId === msg.id;
            const msgDateStr = formatDateBadge(msg.createdAt);

            let showDateBadge = false;
            if (msgDateStr !== lastDateStr) {
              showDateBadge = true;
              lastDateStr = msgDateStr;
            }

            let bubbleClass = isMe ? 'msg-bubble-me' : 'msg-bubble-other';
            if (msg.type === 'voice') bubbleClass = isMe ? 'msg-bubble-voice-me' : 'msg-bubble-voice-other';
            if (msg.type === 'sticker') bubbleClass = `msg-bubble-sticker ${isMe ? 'is-me' : ''}`;

            let msgSenderName = msg.senderName;
            if (!isMe) {
               const sProfile = userProfilesMap[msg.senderId] || targetUserData;
               if (sProfile) {
                  msgSenderName = sProfile.fullName || msgSenderName;
                  if (sProfile.phoneNumber) {
                      const normP = normalizePhone(sProfile.phoneNumber);
                      if (localContacts[normP]) msgSenderName = localContacts[normP];
                  }
               }
            }

            return (
              <React.Fragment key={msg.id}>
                {showDateBadge && <div className="date-badge">{msgDateStr}</div>}
                
                {/* Unread Divider */}
                {firstUnreadIdRef.current === msg.id && totalUnreadCountRef.current > 0 && (
                    <div id={`unread-divider-${msg.id}`} style={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
                        <div style={{ background: '#1e293b', color: '#fff', fontSize: '12px', fontWeight: '700', padding: '6px 16px', borderRadius: '16px', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}>
                            {totalUnreadCountRef.current} unread messages
                        </div>
                    </div>
                )}

                <div 
                  id={`msg-${msg.id}`}
                  className={isHighlighted ? 'msg-blink-container' : ''} 
                  onClick={() => { if (selectedMessageIds.length > 0) toggleSelection(msg.id); }}
                  style={{
                    display: 'flex', flexDirection: 'column', margin: '4px 0', alignItems: isMe ? 'flex-end' : 'flex-start',
                    background: isSelected ? 'rgba(98, 184, 117, 0.22)' : 'transparent',
                    borderRadius: '16px', padding: isSelected ? '4px' : '0', transition: 'background 0.2s ease'
                  }}
                >
                  
                  {!isMe && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                      <div className={isTargetSpecial ? 'anim-coin-flip' : ''} style={{ width: '22px', height: '22px', perspective: '800px' }}>
                        <img src={msg.senderPhoto || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150"} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} alt="U" />
                      </div>
                      
                      <span style={{ fontSize: '13px', color: targetNameColor, fontWeight: '900', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {isTargetOwner ? <span className="gold-name">{msgSenderName}</span> : msgSenderName}
                        {isTargetOwner && <div className="v-badge-3d" style={{width: '14px', height:'14px', fontSize:'9px'}}>V</div>}
                        {(isTargetAdmin || isTargetPremium) && (
                          <svg className="dynamic-shield" viewBox="0 0 100 100">
                            <path className="shield-color" d="M50 5 L90 20 L90 50 C90 75 50 95 50 95 C50 95 10 75 10 50 L10 20 Z"/>
                            <path d="M50 11 L84 24 L84 49 C84 70 50 87 50 87 C50 87 16 70 16 49 L16 24 Z" fill="none" stroke="white" strokeWidth="2.5" opacity="0.8"/>
                            <path d="M32 48 L45 61 L68 38" stroke="white" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </span>
                    </div>
                  )}

                  <div 
                    className={bubbleClass} 
                    style={{ transform: `translateX(${swipeX[msg.id] || 0}px)` }}
                    onClick={(e) => handleMsgClick(e, msg)} 
                    onTouchStart={(e) => handleTouchStartReact(e, msg)} 
                    onTouchMove={(e) => handleTouchMoveReact(e, msg)}
                    onTouchEnd={(e) => handleTouchEndReact(e, msg)}
                  >
                     {/* Reply Box */}
                     {msg.replyToMsgText && (
                        <div 
                          onClick={(e) => { e.stopPropagation(); setHighlightedMsgId(msg.replyToMsgId); document.getElementById(`msg-${msg.replyToMsgId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }); setTimeout(() => setHighlightedMsgId(null), 2400); }}
                          style={{ background: isMe ? '#16a34a' : '#f1f5f9', padding: '6px 10px', borderRadius: '8px', marginBottom: '6px', fontSize: '12px', borderLeft: '4px solid #0f172a', opacity: 0.9, cursor: 'pointer' }}
                        >
                          <strong style={{ display: 'block', marginBottom: '2px', color: isMe ? 'white' : '#0f172a' }}>{msg.replyToSenderName}</strong>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', color: isMe ? 'white' : '#334155' }}>
                            {msg.replyToMsgText}
                          </span>
                        </div>
                      )}

                      {/* Msg Types */}
                      {msg.type === 'text' && <div>{renderTextWithLinks(msg.text)}</div>}
                      
                      {msg.type === 'image' && (
                        <div style={{ padding: '3px' }}>
                          <img src={msg.mediaData} onClick={(e) => { e.stopPropagation(); setFullScreenMedia(msg); }} className="img-3x4" alt="Photo" />
                        </div>
                      )}

                      {msg.type === 'video' && (
                        <video 
                          src={msg.mediaData} 
                          controls 
                          className="img-3x4" 
                          style={{ borderRadius: '12px', outline: 'none' }} 
                        />
                      )}

                      {msg.type === 'sticker' && <img src={msg.mediaData} alt="Sticker" style={{ width: '120px', height: '120px', objectFit: 'contain' }} />}
                      
                      {msg.type === 'file' && (
                        <div>
                          <a href={msg.fileData || msg.fileURL} download={msg.fileName} style={{ color: 'inherit', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '20px' }}>📄</span>
                            <div>
                              <div style={{ fontWeight: '700', fontSize: '13px' }}>{msg.fileName}</div>
                              <div style={{ fontSize: '10px', opacity: 0.8 }}>Download File</div>
                            </div>
                          </a>
                        </div>
                      )}
                      
                      {/* Voice Playback */}
                      {msg.type === 'voice' && (() => {
                        const vp = voicePlayback[msg.id] || {};
                        const dur = Number(vp.duration) || (parseFloat(msg.duration) || 0);
                        const cur = Number(vp.current) || 0;
                        const progressRatio = dur > 0 ? Math.min(1, cur / dur) : 0;
                        const bars = Array.isArray(msg.waveform) && msg.waveform.length
                          ? msg.waveform
                          : makeFallbackWaveform(msg.id);

                        return (
                          <div
                            className={`voice-message-card ${isMe ? 'voice-me' : 'voice-other'}`}
                            onClick={(e) => e.stopPropagation()}
                            style={{ opacity: msg.status === 'uploading' ? 0.82 : 1 }}
                          >
                            <button
                              type="button"
                              className="voice-play-btn"
                              onClick={() => toggleVoicePlayback(msg.id)}
                            >
                              {playingVoiceId === msg.id ? '❚❚' : '▶'}
                            </button>

                            <div className="voice-wave-wrap">
                              <div className="voice-wave">
                                {bars.map((h, i) => (
                                  <span
                                    key={i}
                                    style={{
                                      height: `${h}px`,
                                      background:
                                        i / bars.length <= progressRatio
                                          ? (isMe ? '#ffffff' : '#22c55e')
                                          : (isMe ? 'rgba(255,255,255,.55)' : '#94a3b8')
                                    }}
                                  />
                                ))}
                              </div>
                              <div className="voice-meta">
                                <span>{formatVoiceSeconds(cur || (playingVoiceId === msg.id ? 0 : dur))}</span>
                                <span>{msg.timeStr}</span>
                              </div>
                            </div>

                            <img
                              className="voice-avatar"
                              src={msg.senderPhoto || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=80'}
                              alt=""
                            />

                            <audio
                              id={`voice-${msg.id}`}
                              data-nexora-voice
                              src={msg.audioData}
                              preload="metadata"
                              onPlay={handleAudioPlay}
                              onPause={() => {
                                if (playingVoiceId === msg.id) setPlayingVoiceId(null);
                              }}
                              onLoadedMetadata={(e) => handleVoiceLoadedMetadata(e, msg.id)}
                              onTimeUpdate={(e) => handleVoiceTimeUpdate(e, msg.id)}
                              onEnded={() => handleVoiceEnded(msg.id)}
                              onClick={(e) => e.stopPropagation()}
                              style={{ display: 'none' }}
                            />
                          </div>
                        );
                      })()}

                      {msg.type === 'call_log' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center', justifyContent: 'center', padding: '12px 14px', borderRadius: '18px', background: isMe ? '#dcfce7' : '#f1f5f9', border: '1px solid rgba(148, 163, 184, 0.2)', maxWidth: '260px' }}>
                          <div style={{ fontWeight: '800', color: '#0f172a', textAlign: 'center' }}>
                            {msg.text}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569' }}>
                            <span>{msg.callType === 'video' ? '📹' : '📞'}</span>
                            <span>{msg.duration ? `${Math.floor(msg.duration / 60).toString().padStart(2, '0')}:${(msg.duration % 60).toString().padStart(2, '0')} • ` : ''}{msg.timeStr}</span>
                          </div>
                        </div>
                      )}

                      {/* Magic Meet Card UI */}
                      {msg.type === 'meet_card' && (
                        <div className="magic-meet-wrapper">
                          <div className="magic-meet-header">
                            <h2 className="magic-meet-title">LIVE MEETING</h2>
                            <div className="magic-meet-subtitle">আপনার উপস্থিতি আমাদের জন্য গুরুত্বপূর্ণ</div>
                          </div>
                          <div className="magic-meet-timebox">
                            <div>আজ <b>{msg.meetTime}</b></div>
                            <div style={{ borderLeft: '1px solid #e2e8f0', paddingLeft: '12px' }}>জয়েন হওয়ার সময় <b>১০ মিনিট</b></div>
                          </div>
                          <div className="magic-meet-steps">
                            <div style={{ textAlign: 'center', marginBottom: '8px', color: '#16a34a', fontWeight: '900' }}>জয়েন করার ধাপ:</div>
                            <div className="magic-step-item"><span className="magic-step-no">01</span> Meet এ ক্লিক করুন</div>
                            <div className="magic-step-item"><span className="magic-step-no">02</span> Gmail দিয়ে সাইন ইন করুন</div>
                            <div className="magic-step-item"><span className="magic-step-no">03</span> "Ask to Join" বাটনে ক্লিক করুন</div>
                            <div className="magic-step-item"><span className="magic-step-no">04</span> মিটিংয়ে জয়েন করে স্ক্রিনশট ইনবক্সে পাঠান</div>
                          </div>
                          <button onClick={(e) => { e.stopPropagation(); window.open(msg.meetLink, '_blank'); }} className="magic-join-btn">
                            JOIN MEETING
                          </button>
                        </div>
                      )}

                      {/* Group Invite Magic Card */}
                      {msg.type === 'group_invite' && (
                        <div className="magic-meet-wrapper">
                          <div className="magic-meet-header" style={{ background: '#fef3c7', borderBottom: '2px dashed #fde047' }}>
                            <h2 className="magic-meet-title" style={{ color: '#854d0e' }}>GROUP INVITE</h2>
                            <div className="magic-meet-subtitle" style={{ color: '#a16207', borderColor: '#fde047' }}>আপনাকে গ্রুপে আমন্ত্রণ জানানো হয়েছে</div>
                          </div>
                          <div style={{ padding: '16px', fontWeight: '900', fontSize: '18px', color: '#0f172a' }}>
                            {msg.groupName}
                          </div>
                          <button onClick={(e) => { e.stopPropagation(); navigate('/group', { state: { groupId: msg.inviteGroupId }}); }} className="magic-join-btn" style={{ background: '#ca8a04', boxShadow: '0 4px 15px rgba(202, 138, 4, 0.4)' }}>
                            JOIN GROUP
                          </button>
                        </div>
                      )}

                      {/* Community Invite Magic Card */}
                      {msg.type === 'community_invite' && (
                        <div className="magic-meet-wrapper">
                          <div className="magic-meet-header" style={{ background: '#e0f2fe', borderBottom: '2px dashed #bae6fd' }}>
                            <h2 className="magic-meet-title" style={{ color: '#0284c7' }}>COMMUNITY INVITE</h2>
                            <div className="magic-meet-subtitle" style={{ color: '#0284c7', borderColor: '#bae6fd' }}>আপনাকে কমিউনিটিতে আমন্ত্রণ জানানো হয়েছে</div>
                          </div>
                          <div style={{ padding: '16px', fontWeight: '900', fontSize: '18px', color: '#0f172a' }}>
                            {msg.communityName}
                          </div>
                          <button onClick={(e) => { e.stopPropagation(); joinCommunityInvite(msg.inviteCommunityId); }} className="magic-join-btn" style={{ background: '#0284c7', boxShadow: '0 4px 15px rgba(2, 132, 199, 0.4)' }}>
                            JOIN COMMUNITY
                          </button>
                        </div>
                      )}

                      {/* Time & Status */}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                        {isMsgEditable30s(msg) && <span onClick={(e) => { e.stopPropagation(); setEditingMsg(msg); setEditInputText(msg.text); }} style={{ fontSize: '13px', cursor: 'pointer' }}>✏️</span>}
                        <div style={{ fontSize: '10px', opacity: 0.8, color: msg.type === 'sticker' || msg.type === 'meet_card' || msg.type === 'group_invite' || msg.type === 'community_invite' ? '#64748b' : 'inherit', display: 'flex', alignItems: 'center' }}>
                          {msg.type === 'voice' ? `${msg.duration} • ` : ''} {msg.timeStr} {msg.isEdited && '(edited)'}
                          {renderTickMarks(msg)}
                        </div>
                      </div>

                      {/* Reactions Display */}
                      {msg.reactions && Object.keys(msg.reactions).length > 0 && (
                        <div style={{ position: 'absolute', bottom: '-10px', right: '10px', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '2px 6px', fontSize: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', display: 'flex', gap: '2px', color: '#0f172a' }}>
                          {Object.values(msg.reactions).slice(0, 3).map((r, i) => <span key={i}>{r}</span>)}
                          {Object.keys(msg.reactions).length > 3 && <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#64748b' }}>+{Object.keys(msg.reactions).length - 3}</span>}
                        </div>
                      )}
                  </div>
                </div>
              </React.Fragment>
            );
          })}

          <div ref={messagesEndRef} />
        </div>

        {/* Scroll Bottom Button */}
        {showScrollBottom && (
          <div 
            onClick={() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                autoScrollLockedRef.current = false;
                setShowScrollBottom(false);
            }}
            style={{ position: 'absolute', bottom: '80px', left: '50%', transform: 'translateX(-50%)', width: '40px', height: '40px', background: '#ffffff', borderRadius: '50%', boxShadow: '0 4px 15px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 25 }}
          >
            <span style={{ fontSize: '18px' }}>⬇️</span>
          </div>
        )}

        {/* ৪. ফিক্সড ইনপুট ফুটার বার */}
        <div className="footer-container">
          {hasBlockedTarget ? (
            <div style={{ padding: '16px', textAlign: 'center', background: '#fef2f2', borderTop: '1px solid #fee2e2', color: '#ef4444', fontWeight: 'bold', fontSize: '14px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <span>আপনি এই ইউজারকে ব্লক করেছেন।</span>
              <button onClick={handleUnblockUser} style={{ background: '#ef4444', color: 'white', border: 'none', padding: '6px 16px', borderRadius: '12px', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer' }}>
                Unblock
              </button>
            </div>
          ) : isBlockedByTarget ? (
            <div style={{ padding: '20px', textAlign: 'center', background: '#f8fafc', borderTop: '1px solid #f1f5f9', color: '#64748b', fontWeight: '600', fontSize: '13px' }}>
              আপনি এই ইউজারকে মেসেজ পাঠাতে পারবেন না।
            </div>
          ) : (
            <>
              {(replyingToMsg || editingMsg) && (
                <div style={{ background: '#f8fafc', padding: '8px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f1f5f9' }}>
                  <div>
                    <span style={{ fontSize: '12px', fontWeight: '800', color: '#2563eb', display: 'block' }}>
                      {editingMsg ? 'Editing message' : `Replying to ${replyingToMsg.senderName || 'User'}`}
                    </span>
                    <span style={{ fontSize: '12px', color: '#64748b', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '280px' }}>
                      {editingMsg ? editInputText : getReplySummaryText(replyingToMsg)}
                    </span>
                  </div>
                  <button onClick={() => { setReplyingToMsg(null); setEditingMsg(null); setText(''); if (textareaRef.current) textareaRef.current.style.height = '20px'; }} style={{ background: 'none', border: 'none', fontSize: '16px', cursor: 'pointer', color: '#64748b' }}>✕</button>
                </div>
              )}

              <form onSubmit={handleSendMessage} className="chat-input-form">
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', background: '#f0f2f5', borderRadius: '24px', padding: '0 12px', minHeight: '40px' }}>
                  <span onClick={() => setShowStickerModal(true)} style={{ cursor: 'pointer', fontSize: '18px', marginRight: '6px' }}>😊</span>

                  <textarea
                    ref={textareaRef}
                    rows="1"
                    placeholder="Message"
                    value={text}
                    onChange={handleInputChange}
                    onPaste={handlePaste}
                    style={{ flex: 1, padding: '10px 0', background: 'none', border: 'none', color: '#0f172a', outline: 'none', fontSize: '14px', resize: 'none', maxHeight: '120px', overflowY: 'auto' }}
                  />

                  <button type="button" onClick={() => fileInputRef.current?.click()} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', paddingBottom: '2px' }} title="Attach Document">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                  </button>

                  <button type="button" onClick={() => mediaInputRef.current?.click()} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', paddingBottom: '2px' }} title="Attach Photo/Video">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                  </button>
                </div>

                {text.trim() || editingMsg ? (
                  <button type="submit" className="black-action-btn" title="Send" style={{ marginBottom: '0px' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" style={{ transform: 'translateY(1px)' }}><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                  </button>
                ) : (
                  <button type="button" onClick={openVoiceModal} className="black-action-btn" title="Voice Note" style={{ marginBottom: '0px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" style={{ transform: 'translateY(1px)' }}><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                  </button>
                )}
              </form>
            </>
          )}
        </div>

      </div>
    </div>
  );
};

const styles = {
  container: { height: '100dvh', width: '100vw', overflow: 'hidden', backgroundColor: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center' }
};

export default ChatScreen;